// Build-stamp bust the vite content-hash when we need clients to drop a
// cached bundle that shares the same content shape. Change freely.
const __BUILD_STAMP = 'v1.0.418-2026-04-22-ts'
if (typeof window !== 'undefined') { (window as any).__APP_BUILD = __BUILD_STAMP }
import { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react'
import { IS_BOOSTER_ONLY, IS_PROFILER_ONLY, HAS_PROFILER } from './product'
import DevEngineSwitcher from './components/DevEngineSwitcher'
import { useUserPref } from './hooks/useUserPref'
import { SIDEBAR_HIDDEN_PREF_KEY, normalizeHiddenSet } from './navItems'
import {
  login,
  LogEntry, WsState, RunStats, TimelinePoint, CampaignStats, CampaignSummary,
  Settings, SysInfo, HourlyPoint, HourlyCampaignPoint,
  startCampaign, stopCampaign, pauseCampaign, resumeCampaign,
  updateCampaign, deleteCampaign, reorderCampaigns,
  exportCampaignConfig, importCampaignConfig,
  uploadCampaignFolder, getCampaignFiles,
  getProfilerConfig, saveProfilerConfig, startProfiler, stopProfiler,
  getProfilerCampaign, updateProfilerCampaign, startProfilerCampaign, stopProfilerCampaign,
  getProfilerCaptchaDetail,
  getLicenseInfo, type LicenseInfo,
} from './api'
import { useAccumulatedStats } from './useLogStats'
import { useTheme } from './theme'

import LoginPage from './components/LoginPage'
import { CreepLogo } from './components/CreepLogo'
import { CreepMark } from './components/CreepMark'
import { LicenseBadge } from './components/LicenseBadge'
import ProjectsPage from './components/ProjectsPage'
import BoosterOverview from './components/BoosterOverview'
import PanelSettings from './components/PanelSettings'
import AlarmModal, { AlarmPayload } from './components/AlarmModal'
import LiveNotificationToast, { type LivePushNotification } from './components/LiveNotificationToast'
import { ConfirmDialogHost, confirm as appConfirm } from './components/ConfirmDialog'
import ServerSwitcher from './components/ServerSwitcher'
import BalanceBanner from './components/BalanceBanner'
import ToolsPage from './components/ToolsPage'
import IdeasPage from './components/IdeasPage'
import BugsPage from './components/BugsPage'
import {AssistantWidget} from './components/AssistantWidget'
import AdminDocs from './components/AdminDocs'
import ChangelogModal from './components/ChangelogModal'
import { MagicCursorCanvas } from './components/MagicCursorCanvas'
import BottomPanel from './components/BottomPanel'
import ProjectModal, { type ProjectModalResult } from './components/ProjectModal'
import DynamicTabBooster from './components/DynamicTabBooster'
import DynamicTab from './components/DynamicTab'
import DynamicTabProfiler from './components/DynamicTabProfiler'
import ProfilerProjectsPage from './components/ProfilerProjectsPage'
import ProfilerProjectModal from './components/ProfilerProjectModal'
import ProfilerOverview from './components/ProfilerOverview'
import ProjectTabsBar, {
  type ProjectTab,
  loadProjectTabs,
  saveProjectTabs,
  upsertProjectTab,
  closeProjectTab,
} from './components/ProjectTabsBar'
import SchedulerDialog from './components/SchedulerDialog'
import { ResultsStats, WorkersTable } from './components/ResultsView'
import { getVisibleBoosterSections, getBoosterDefaults, type BoosterSectionName } from './resources_booster'
import { SECTIONS as PROFILER_SECTIONS, type SectionName as ProfilerSectionName } from './resources'
import { isProfilerFieldVisible, getProfilerFieldsForSection, getProfilerDefaults } from './resources_profiler'

// Секции профайлера, в которых есть хотя бы одно видимое поле при текущем
// конфиге. В удалённом режиме (SERVER_REMOTE_STATUS=Включено) остаётся только
// «Основные настройки» — как в BAS, где была одна вкладка с идентификатором.
function getVisibleProfilerSections(cfg: Record<string, any>, isAdmin: boolean): string[] {
  return ([...PROFILER_SECTIONS] as string[]).filter(s =>
    getProfilerFieldsForSection(s as any, isAdmin).some(([key]) => isProfilerFieldVisible(key, cfg)),
  )
}

import {
  LayoutDashboard, FolderOpen, Settings2, Wrench, Lightbulb, BookOpen, Key, Rocket,
  Play, Square, Pause, Upload, Download, FileText, Wand2, FolderUp,
  Moon, Sun, PanelLeftClose, PanelLeft, LogOut, Sparkles, Loader2,
  AlertCircle, CheckCircle2, ChevronDown, Pencil, Trash2, Calendar,
  AlertTriangle, HelpCircle, Search, Globe, Shield, Brain, Bell, Bug,
  BarChart3, Save, Fingerprint, Menu, X, RotateCcw, MoreVertical, Clock,
  Megaphone, Newspaper, Server, Workflow, IdCard,
} from 'lucide-react'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import {
  Sheet, SheetContent, SheetTrigger,
} from '@/components/ui/sheet'

const _stub = () => null
// Админка (лицензии/деплой/модерация/рабочие серверы) не нужна ни клиенту
// (booster), ни нашему рабочему серверу (profiler) — только на pushka (full).
const _noAdmin = IS_BOOSTER_ONLY || IS_PROFILER_ONLY
const LicenseAdmin = _noAdmin ? _stub : lazy(() => import('./components/LicenseAdmin'))
// Разделы модерации (идеи/баги/новости) доступны и в booster-сборке —
// глобальный сотрудник со staff-токеном модерирует с клиентской панели
// (бэкенд-прокси форвардит X-Staff-Token на лиц-сервер). Видимость
// контролируется _hasModerationPerm в сайдбаре.
const NewsAdmin = IS_PROFILER_ONLY ? _stub : lazy(() => import('./components/NewsAdmin'))
// Редактор сценария — рабочий инструмент САМОГО профайлера, а не «админка»:
// вся логика прокачки живёт в дереве кубиков, и оператор обязан видеть её на
// том сервере, где прокачка идёт. В profiler-сборке он был заглушён вместе с
// лицензиями/деплоем, при этом пункт меню оставался (гейт по суперадмину) —
// клик открывал пустоту, и казалось, что сценария «нет». Стабим только в
// booster-сборке, где профайлера нет вовсе.
const ScenarioEditorAdmin = IS_BOOSTER_ONLY ? _stub : lazy(() => import('./components/ScenarioEditorAdmin'))
const EventsPage = IS_PROFILER_ONLY ? _stub : lazy(() => import('./components/EventsPage'))
const DeploymentManager = _noAdmin ? _stub : lazy(() => import('./components/DeploymentManager'))
const WorkServersAdmin = _noAdmin ? _stub : lazy(() => import('./components/WorkServersAdmin'))
const FilesEditorModal = lazy(() => import('./components/FilesEditorModal'))
const TaskGeneratorModal = lazy(() => import('./components/TaskGeneratorModal'))
const FingerprintsPage = _noAdmin ? _stub : lazy(() => import('./components/FingerprintsPage'))
const IdentifiersAdmin = _noAdmin ? _stub : lazy(() => import('./components/IdentifiersAdmin'))
const IdentifiersPage = lazy(() => import('./components/IdentifiersPage'))
const IdeasAdmin = IS_PROFILER_ONLY ? _stub : lazy(() => import('./components/IdeasAdmin'))
const BugsAdmin = IS_PROFILER_ONLY ? _stub : lazy(() => import('./components/BugsAdmin'))
const AiTicketsAdmin = _noAdmin ? _stub : lazy(() => import('./components/AiTicketsAdmin'))
const ServerOrdersPage = IS_PROFILER_ONLY ? _stub : lazy(() => import('./components/ServerOrdersPage'))
const ServerOrdersAdmin = _noAdmin ? _stub : lazy(() => import('./components/ServerOrdersAdmin'))

interface AlertMsg { type: 'success' | 'error'; text: string }

async function fetchMe() {
  const r = await fetch('/api/me')
  if (!r.ok) throw new Error('unauthorized')
  return r.json() as Promise<{
    user: string; user_id: string; access_level: string
    available_products: string[]; version: string; version_profiler?: string; license_valid: boolean
    suspended?: boolean; _internal_docs?: boolean; is_superadmin?: boolean; is_license_server?: boolean; has_staff_access?: boolean
    pending_alarm?: { id: number; severity: string; title: string; body: string;
                      expires_at?: number; created_at?: number } | null
  }>
}

async function createCampaignApi(data: Record<string, unknown>) {
  const r = await fetch('/api/booster/campaigns', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  })
  return r.json()
}

async function createProfilerCampaignApi(data: Record<string, unknown>) {
  const r = await fetch('/api/profiler/campaigns', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  })
  return r.json()
}

async function getCampaign(id: string) {
  const r = await fetch(`/api/booster/campaigns/${id}`)
  return r.json()
}


async function doLogout() { await fetch('/api/logout', { method: 'POST' }) }

const SECTION_ICONS: Record<string, typeof Settings2> = {
  'Настройки поиска': Search,
  'Уведомления Telegram': Bell,
  'Прокси и анонимность': Shield,
  'Настройки сайта': Globe,
  'Решение капчи': AlertCircle,
  'Баг-Чекер': Bug,
  'Основные настройки': Settings2,
  'Умный режим': Brain,
  'Сценарий': Workflow,
}

const COLOR_PALETTE = [
  { value: '', hex: '#3B82F6' },
  { value: '#3b82f6', hex: '#3b82f6' },
  { value: '#22c55e', hex: '#22c55e' },
  { value: '#8b5cf6', hex: '#8b5cf6' },
  { value: '#ef4444', hex: '#ef4444' },
  { value: '#eab308', hex: '#eab308' },
]

export default function App() {
  const { mode, toggleMode } = useTheme()
  const [loggedIn, setLoggedIn] = useState<boolean | null>(null)
  const [accessLevel, setAccessLevel] = useState('admin')
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [isLicenseServer, setIsLicenseServer] = useState(false)
  const [hasStaffAccess, setHasStaffAccess] = useState(false)
  const [availableProducts, setAvailableProducts] = useState<string[]>(['booster'])
  const [userPerms, setUserPerms] = useState<string[]>([])
  const [appVersion, setAppVersion] = useState('')
  const [profilerVersion, setProfilerVersion] = useState('')
  const [isSuspended, setIsSuspended] = useState(false)
  const [restrictBugs, setRestrictBugs] = useState(false)
  const [restrictIdeas, setRestrictIdeas] = useState(false)
  const [pendingAlarm, setPendingAlarm] = useState<AlarmPayload | null>(null)

  useEffect(() => {
    fetchMe().then(data => {
      setLoggedIn(true)
      setAccessLevel(data.access_level)
      setIsSuperAdmin(!!data.is_superadmin)
      setIsLicenseServer(!!data.is_license_server)
      setHasStaffAccess(!!data.has_staff_access)
      setAvailableProducts(data.available_products || [])
      setUserPerms(Array.isArray(data.permissions) ? data.permissions : [])
      setAppVersion(data.version || '')
      setProfilerVersion(data.version_profiler || data.version || '')
      setIsSuspended(!!data.suspended)
      setRestrictBugs(!!data.restrict_bugs)
      setRestrictIdeas(!!data.restrict_ideas)
      setPendingAlarm(data.pending_alarm || null)
    }).catch(() => setLoggedIn(false))
  }, [])

  useEffect(() => {
    if (!loggedIn || !isSuspended) return
    const iv = setInterval(() => {
      fetchMe().then(data => {
        if (!data.suspended) setIsSuspended(false)
      }).catch(() => {})
    }, 30000)
    return () => clearInterval(iv)
  }, [loggedIn, isSuspended])

  // Poll /api/me at a low frequency just to surface fresh alarms. The
  // panel's heartbeat-driven cache is updated on its own ~30s schedule
  // server-side; polling every 60s ensures the modal appears within
  // ~1.5 min of an admin pushing it.
  useEffect(() => {
    if (!loggedIn) return
    const iv = setInterval(() => {
      fetchMe().then(data => {
        // Only update if state actually changed; avoids re-mounting the
        // modal mid-interaction.
        const current = pendingAlarm?.id || 0
        const next = data.pending_alarm?.id || 0
        if (next !== current) setPendingAlarm(data.pending_alarm || null)
      }).catch(() => {})
    }, 60_000)
    return () => clearInterval(iv)
  }, [loggedIn, pendingAlarm])

  const handleLogout = useCallback(async () => { await doLogout(); setLoggedIn(false) }, [])

  if (loggedIn === null) {
    return <div className="h-[100dvh] flex items-center justify-center bg-background text-muted-foreground">Loading...</div>
  }

  if (!loggedIn) {
    return <LoginPage onSuccess={() => {
      setLoggedIn(true)
      fetchMe().then(data => {
        setAccessLevel(data.access_level)
        setIsSuperAdmin(!!data.is_superadmin)
        setIsLicenseServer(!!data.is_license_server)
        setHasStaffAccess(!!data.has_staff_access)
        setAvailableProducts(data.available_products || [])
        setUserPerms(Array.isArray(data.permissions) ? data.permissions : [])
        setAppVersion(data.version || '')
        setProfilerVersion(data.version_profiler || data.version || '')
        setIsSuspended(!!data.suspended)
        setRestrictBugs(!!data.restrict_bugs)
        setRestrictIdeas(!!data.restrict_ideas)
      })
    }} />
  }

  if (isSuspended) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center">
        <div className="bg-card border border-border rounded-2xl p-10 max-w-md text-center shadow-2xl">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-amber-500/10 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-500"><circle cx="12" cy="12" r="10"/><line x1="10" y1="15" x2="10" y2="9"/><line x1="14" y1="15" x2="14" y2="9"/></svg>
          </div>
          <h2 className="text-xl font-bold mb-2">Лицензия приостановлена</h2>
          <p className="text-muted-foreground text-sm">Работа панели временно приостановлена администратором. Все проекты остановлены.</p>
          <p className="text-muted-foreground text-xs mt-4">Обратитесь к администратору для возобновления.</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <AppPanel
        theme={mode}
        toggleTheme={toggleMode}
        onLogout={handleLogout}
        accessLevel={accessLevel}
        isSuperAdmin={isSuperAdmin}
        availableProducts={availableProducts}
        appVersion={appVersion}
        profilerVersion={profilerVersion}
        userPerms={userPerms}
        isLicenseServer={isLicenseServer}
        hasStaffAccess={hasStaffAccess}
        restrictBugs={restrictBugs}
        restrictIdeas={restrictIdeas}
      />
      {pendingAlarm && (
        <AlarmModal alarm={pendingAlarm} onDismiss={() => setPendingAlarm(null)} />
      )}
    </>
  )
}

interface AppPanelProps {
  theme: string; toggleTheme: () => void; onLogout: () => void
  accessLevel: string; isSuperAdmin: boolean; availableProducts: string[]; appVersion: string
  profilerVersion: string
  userPerms: string[]; isLicenseServer: boolean; hasStaffAccess: boolean
  restrictBugs: boolean; restrictIdeas: boolean
}

function AppPanel({ theme, toggleTheme, onLogout, accessLevel, isSuperAdmin, availableProducts, appVersion, profilerVersion, userPerms, isLicenseServer, hasStaffAccess, restrictBugs, restrictIdeas }: AppPanelProps) {
  const isOperator = accessLevel === 'operator'
  const canFull = accessLevel === 'admin' || accessLevel === 'full'
  const canEdit = canFull || accessLevel === 'editor'
  const canLicenses = accessLevel === 'admin' || isOperator
  const isAdminAccess = accessLevel === 'admin'

  // Granular permission check для UI: super-admin видит всё; иначе
  // строго по permissions массиву из /api/me.
  // Если массив пуст (legacy юзеры до миграции) — fallback на старые флаги
  // через access_level (только для admin/full — иначе они теряют доступ
  // вообще ко всему). Для нового кода используем _hasPerm.
  const _hasPerm = useCallback((key: string): boolean => {
    if (isSuperAdmin) return true
    if (userPerms.length > 0) return userPerms.includes(key)
    // Legacy fallback: admin/full видят всё
    if (accessLevel === 'admin' || accessLevel === 'full') return true
    // Базовый набор для остальных уровней
    const FALLBACK: Record<string, string[]> = {
      operator: ['dashboard', 'projects', 'identifiers', 'settings', 'tools', 'events',
                  'ideas', 'bugs', 'server_orders', 'licenses', 'server_orders_admin'],
      editor:   ['dashboard', 'projects', 'identifiers', 'settings', 'tools', 'events',
                  'ideas', 'bugs', 'server_orders'],
      manager:  ['dashboard', 'projects', 'identifiers', 'tools', 'events',
                  'ideas', 'bugs', 'server_orders'],
    }
    return (FALLBACK[accessLevel] || FALLBACK.manager).includes(key)
  }, [isSuperAdmin, userPerms, accessLevel])

  // Админские (pushka-only) разделы: управляют ВСЕЙ инфраструктурой и имеют
  // смысл только на центральном сервере. Видны если ты super-admin (наша
  // главная учётка — везде) ЛИБО это центральный сервер pushka. Клиентский
  // владелец на своём сервере их не видит.
  const _hasCentralPerm = useCallback((key: string): boolean => {
    return _hasPerm(key) && (isSuperAdmin || isLicenseServer)
  }, [_hasPerm, isSuperAdmin, isLicenseServer])

  // Разделы модерации (идеи/баги/новости) дополнительно доступны
  // глобальному сотруднику со staff-токеном — он может модерировать с
  // клиентской панели (лиц-сервер пускает его по подписанному токену).
  const _hasModerationPerm = useCallback((key: string): boolean => {
    return _hasPerm(key) && (isSuperAdmin || isLicenseServer || hasStaffAccess)
  }, [_hasPerm, isSuperAdmin, isLicenseServer, hasStaffAccess])

  // Редактор сценария на рабочем профайлер-сервере. Суперадминами жёстко
  // считаются только `admin` и `cr33p_svc`, а в пул мы заходим под учёткой
  // `work` — с гейтом по суперадмину пункт меню там не показывался вообще,
  // и дерево прокачки было недоступно на самом сервере прокачки. Такие
  // серверы наши внутренние (сборка profiler), клиентам это не видно:
  // в booster-сборке компонент заглушён.
  // Редактор сценария прокачки — ТОЛЬКО супер-админ (главная учётка admin/cr33p_svc)
  // либо аккаунт, которому ЯВНО выдали право scenario_editor. Раньше его видел
  // любой уровень `admin` на профайлер-сервере (в т.ч. рабочий аккаунт `work`,
  // под которым заходят помощники) — убрали по требованию владельца: дерево
  // прокачки правит только главная учётка. `_hasCentralPerm` на профайлер-сервере
  // (isLicenseServer=false) для не-супера всегда false → у `work` редактор скрыт;
  // на pushka можно осознанно выдать право scenario_editor доверенному аккаунту.
  const _canEditScenario = isSuperAdmin || _hasCentralPerm('scenario_editor')

  // Polling бейджа «оплачено-но-не-выполнено» — раз в 45 сек.
  // Только для админа. Тихо игнорируется на клиентских серверах (403/403).
  useEffect(() => {
    if (!isAdminAccess || IS_BOOSTER_ONLY) return
    let cancelled = false
    const fetchBadge = async () => {
      if (tabSleeping) return
      try {
        const r = await fetch('/api/admin/server-orders/badge')
        if (!r.ok) return
        const d = await r.json()
        if (!cancelled) setOrdersPaidPending(Number(d.total_paid_pending || 0))
      } catch { /* silent */ }
    }
    fetchBadge()
    const t = setInterval(fetchBadge, 45_000)
    return () => { cancelled = true; clearInterval(t) }
  }, [isAdminAccess])
  const hasBooster = availableProducts.includes('booster')
  const hasProfiling = availableProducts.includes('profiler')
  const hasBothProducts = hasProfiling && hasBooster
  const [activeProduct, setActiveProduct] = useState<string>(() => {
    if (!hasProfiling) return 'booster'
    const saved = localStorage.getItem('cb-panel-mode')
    if (saved === 'booster' && hasBooster) return 'booster'
    if (saved === 'profiler' && hasProfiling) return 'profiler'
    return hasBooster ? 'booster' : 'profiler'
  })

  // Реконсиляция: availableProducts приходит из /api/me асинхронно ПОСЛЕ
  // первого рендера (initial default = ['booster']). Если активный продукт
  // недоступен на этом сервере (напр. profiler-only сервер, а activeProduct
  // залип в 'booster') — переключаемся на доступный. Иначе профайлер-сервер
  // показывал бы бустер-режим.
  useEffect(() => {
    if (!availableProducts.length) return
    if (!availableProducts.includes(activeProduct)) {
      setActiveProduct(availableProducts.includes('profiler') && !availableProducts.includes('booster')
        ? 'profiler' : availableProducts[0])
    }
  }, [availableProducts, activeProduct])

  const [selectedSection, setSelectedSection] = useState<BoosterSectionName | ProfilerSectionName>('Основные настройки')
  const [selectedCfg, setSelectedCfg] = useState<Settings>({})
  const [profilerCfg, setProfilerCfg] = useState<Settings>({})
  const [profilerRunning, setProfilerRunning] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const [logs, setLogs] = useState<LogEntry[]>([])
  const allLogsRef = useRef<LogEntry[]>([])
  // Live admin notifications pushed via WS (`t: 'notification_push'`).
  // Rendered as a persistent modal that the user must explicitly
  // acknowledge — see LiveNotificationToast.
  const [livePushQueue, setLivePushQueue] = useState<LivePushNotification[]>([])
  const livePushDismissedRef = useRef<Set<number>>(new Set())
  const [wsState, setWsState] = useState<WsState>({})
  const [wsStats, setWsStats] = useState<RunStats>({})
  const [boosterProjects, setBoosterProjects] = useState<CampaignSummary[]>([])
  const [profilerProjects, setProfilerProjects] = useState<CampaignSummary[]>([])
  // Активный список проектов меняется в зависимости от продукта.
  // Один общий `projects` используется во всём UI — это позволяет
  // переиспользовать ProjectsPage / Overview и т.п. без дублирования.
  // ВАЖНО: derived должно быть ВЫШЕ всех useMemo/useCallback которые
  // имеют `projects` в deps, иначе TDZ ReferenceError при render.
  const projects: CampaignSummary[] = activeProduct === 'profiler' ? profilerProjects : boosterProjects
  const [timeline, setTimeline] = useState<TimelinePoint[]>([])
  const [hourlyHistory, setHourlyHistory] = useState<HourlyPoint[]>([])
  const [hourlyCampaignHistory, setHourlyCampaignHistory] = useState<HourlyCampaignPoint[]>([])
  const [campaignStats, setCampaignStats] = useState<Record<string, CampaignStats>>({})
  // Разрез аналитики капчи по кампаниям (вкладка «Капча»). Отдельно от
  // campaignStats: приходит медленным каналом раз в минуту.
  const [captchaDetail, setCaptchaDetail] = useState<Record<string, any>>({})
  // Состав пачки профилей по кампаниям (срез раз в час с сервера).
  const [poolStats, setPoolStats] = useState<Record<string, any>>({})
  const [sysInfo, setSysInfo] = useState<SysInfo | null>(null)
  const [vpnStatus, setVpnStatus] = useState<{ connected: boolean; vpn_ip?: string; endpoint?: string; endpoint_host?: string; name?: string; engine?: string } | null>(null)
  const [licInfo, setLicInfo] = useState<LicenseInfo | null>(null)
  useEffect(() => {
    let alive = true
    const load = () => getLicenseInfo().then(d => { if (alive) setLicInfo(d) }).catch(() => {})
    load()
    const iv = setInterval(load, 6 * 3600 * 1000)  // дни меняются медленно — раз в 6ч
    return () => { alive = false; clearInterval(iv) }
  }, [])
  const [availableUpdate, setAvailableUpdate] = useState<any>(null)

  const [actionInProgress, setActionInProgress] = useState<string | null>(null)
  const [alert, setAlert] = useState<AlertMsg | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  const [selectedProject, setSelectedProject] = useState<string | null>(null)
  const [campaignFiles, setCampaignFiles] = useState<{ sites_file: any; key_files: any[] }>({ sites_file: null, key_files: [] })
  const [cfgLoading, setCfgLoading] = useState(false)
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null)
  const [editingProject, setEditingProject] = useState<any>(null)
  const [projectModalOpen, setProjectModalOpen] = useState(false)
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null)
  // Быстрые вкладки недавно открытых проектов (persist per product).
  const [projectTabs, setProjectTabs] = useState<ProjectTab[]>(() =>
    loadProjectTabs(IS_PROFILER_ONLY ? 'profiler' : 'booster'),
  )

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === '1')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showChangelog, setShowChangelog] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [fileEditorTarget, setFileEditorTarget] = useState<string | null>(null)
  const [taskGeneratorTarget, setTaskGeneratorTarget] = useState<string | null>(null)
  const [showSchedule, setShowSchedule] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showLicenses, setShowLicenses] = useState(false)
  const [showWorkServers, setShowWorkServers] = useState(false)
  const [showAlarmsAdmin, setShowAlarmsAdmin] = useState(false)
  const [showScenarioEditor, setShowScenarioEditor] = useState(false)
  const [showEvents, setShowEvents] = useState(false)
  const [selectedNewsId, setSelectedNewsId] = useState<number | null>(null)
  const [selectedNewsTitle, setSelectedNewsTitle] = useState<string>('')
  const [eventsUnread, setEventsUnread] = useState<number>(0)
  const [showDeployments, setShowDeployments] = useState(false)
  const [showDocs, setShowDocs] = useState(false)
  const [showTools, setShowTools] = useState(false)
  const [showIdeas, setShowIdeas] = useState(false)
  const [showIdeasAdmin, setShowIdeasAdmin] = useState(false)
  const [ideasVotingCount, setIdeasVotingCount] = useState<number>(0)
  const [selectedIdeaId, setSelectedIdeaId] = useState<number | null>(null)
  const [selectedIdeaTitle, setSelectedIdeaTitle] = useState<string>('')
  const [showBugs, setShowBugs] = useState(false)
  const [showBugsAdmin, setShowBugsAdmin] = useState(false)
  const [showAiTickets, setShowAiTickets] = useState(false)
  const [showServerOrders, setShowServerOrders] = useState(false)
  const [showServerOrdersAdmin, setShowServerOrdersAdmin] = useState(false)
  // Бейдж для сайдбара: сколько заявок оплачено но не выполнено
  const [ordersPaidPending, setOrdersPaidPending] = useState(0)
  const [bugsNewCount, setBugsNewCount] = useState<number>(0)
  // Unread admin-reply counters: number of bugs/ideas with admin replies
  // newer than the last time the user opened the corresponding section.
  // Last-seen ts is persisted in localStorage as `bugs-last-seen-ts`
  // and `ideas-last-seen-ts` (unix-seconds).
  const [bugsUnreadReplies, setBugsUnreadReplies] = useState<number>(0)
  const [ideasUnreadReplies, setIdeasUnreadReplies] = useState<number>(0)
  const [selectedBugId, setSelectedBugId] = useState<number | null>(null)
  const [selectedBugTitle, setSelectedBugTitle] = useState<string>('')
  const [selectedToolSlug, setSelectedToolSlug] = useState<string | null>(null)
  const [showFingerprints, setShowFingerprints] = useState(false)
  const [showIdentifiersAdmin, setShowIdentifiersAdmin] = useState(false)
  const [showIdentifiers, setShowIdentifiers] = useState(false)
  const [showProjects, setShowProjects] = useState(false)
  const [showProfilerProjects, setShowProfilerProjects] = useState(false)
  const [showProfilerOverview, setShowProfilerOverview] = useState(false)
  const [hasInternalDocs, setHasInternalDocs] = useState(false)

  const [rightTab, setRightTab] = useState<'settings' | 'results'>(() =>
    localStorage.getItem('cb-booster-right-tab') === 'results' ? 'results' : 'settings',
  )
  const [serverName, setServerName] = useState(() => localStorage.getItem('cb-server-name') || '')
  const [allWorkers, setAllWorkers] = useState<any[]>([])
  const [sidebarActionId, setSidebarActionId] = useState<string | null>(null)
  // Серверы из dashboard-init → передаём в ServerSwitcher как initialServers
  const [myServersList, setMyServersList] = useState<any[]>([])

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>()
  const uploadRef = useRef<HTMLInputElement>(null)
  const prevRunningRef = useRef(false)
  const projectCacheRef = useRef<Record<string, { cfg: Settings; files: any; ts: number }>>({})
  // Mirrors of selectedProject + isDirty state; used inside async fetch
  // callbacks (loadProject background re-fetch) where reading the React
  // state via closure would give us a stale value from when the callback
  // was created. See bug #8.
  const selectedProjectRef = useRef<string | null>(null)
  const isDirtyRef = useRef(false)
  // Monotonic token bumped on every loadProject() call. An async fetch
  // checks this after `await` and bails if a newer load started — prevents
  // a slow in-flight response from clobbering the cfg of a project the user
  // already switched to (foreground race, mirror of the bg-refetch guard).
  const loadSeqRef = useRef(0)

  const { stats: accStats, ingest: ingestLogs, reset: resetStats } = useAccumulatedStats()

  const enrichedStats = useMemo(() => {
    const ws = wsStats as any
    const enriched = { ...accStats }

    // Для per-campaign продуктов берём статы из campaignStats[cid],
    // а не из глобальных wsStats.success/fail (которые суммируют всё).
    if (selectedProject && campaignStats[selectedProject]) {
      const cs = campaignStats[selectedProject] as any
      enriched.success = Number(cs.ok) || 0
      enriched.fail = Number(cs.fail) || 0
      enriched.captchaTotal = Number(cs.captcha) || 0
    } else {
      if (typeof ws.success === 'number' && ws.success > enriched.success)
        enriched.success = ws.success
      if (typeof ws.fail === 'number' && ws.fail > enriched.fail)
        enriched.fail = ws.fail
    }
    if (typeof ws.recovered === 'number' && ws.recovered > enriched.recovered)
      enriched.recovered = ws.recovered
    // Captcha counter — bug #23 / #37: when a project is selected, the
    // bottom panel must show captchas for THIS campaign only, never the
    // global sum across all running campaigns. Backend streams the
    // per-campaign breakdown via `campaign_stats[cid].captcha`.
    //
    // Bug #37 follow-up: the previous else-fallback to `ws.captcha`
    // leaked the global counter into per-campaign view during the brief
    // window right after `start_campaign` (where the backend pops
    // `_campaign_stats[cid]`) and during WS reconnects, so a freshly
    // started campaign briefly showed the panel-wide total. Force 0
    // until the per-cid entry materialises.
    // Тот же случай, что и с профайлером ниже: `campaign_stats` приходит
    // ВЕРХНИМ уровнем WS-сообщения, а `ws` здесь — это `d.stats`. Обращение к
    // `ws.campaign_stats` всегда давало undefined, из-за чего при выбранном
    // проекте счётчик капчи принудительно обнулялся (ветка else ниже).
    const csByCid = campaignStats as any
    if (selectedProject) {
      if (csByCid && typeof csByCid === 'object' && csByCid[selectedProject]) {
        enriched.captchaTotal = Number(csByCid[selectedProject].captcha) || 0
      } else {
        enriched.captchaTotal = 0
      }
    } else if (typeof ws.captcha === 'number' && ws.captcha > enriched.captchaTotal) {
      enriched.captchaTotal = ws.captcha
    }

    // Prefer per-campaign grouped (server v1.0.481+) when a project is
    // selected, so MELLSTROY's Results don't pile in with ZOOMA's. If
    // per-cid maps are absent (older server) — fall back to the global
    // *_grouped which is the legacy combined view.
    const okByCid = ws.ok_grouped_by_cid
    const failByCid = ws.fail_grouped_by_cid
    const tempByCid = ws.temp_grouped_by_cid
    const skipByCid = ws.skip_grouped_by_cid  // Idea #69: SmartPilot skips
    if (selectedProject && okByCid && typeof okByCid === 'object') {
      enriched.okGrouped = okByCid[selectedProject] || {}
      enriched.failGrouped = (failByCid && failByCid[selectedProject]) || {}
      enriched.tempGrouped = (tempByCid && tempByCid[selectedProject]) || {}
      enriched.skipGrouped = (skipByCid && skipByCid[selectedProject]) || {}
    } else {
      if (ws.ok_grouped) enriched.okGrouped = ws.ok_grouped
      if (ws.fail_grouped) enriched.failGrouped = ws.fail_grouped
      if (ws.temp_grouped) enriched.tempGrouped = ws.temp_grouped
      if (ws.skip_grouped) enriched.skipGrouped = ws.skip_grouped
    }

    // Profiler grouped «данные по прокачке» — always campaign-scoped (no
    // global mirror). Harmless when the selected project is a booster cid
    // (key simply won't exist). ReportPanel reads these only in profiler mode.
    const pOkByCid = ws.profiler_ok_grouped_by_cid
    const pFailByCid = ws.profiler_fail_grouped_by_cid
    if (selectedProject) {
      enriched.profilerOkGrouped = (pOkByCid && pOkByCid[selectedProject]) || {}
      enriched.profilerFailGrouped = (pFailByCid && pFailByCid[selectedProject]) || {}
    }

    // Authoritative per-campaign profiler counters (persisted in campaign_runtime,
    // same source as the top result tiles). The grouped accumulators above are
    // in-memory only and reset to ~0 on every panel restart/deploy — which made
    // «Результаты» show a truncated number (e.g. 329) while the tiles showed the
    // full cumulative count (e.g. 4214). Feed the authoritative counters so the
    // Results headline matches the dashboard.
    // ВАЖНО: per-campaign статы приходят ВЕРХНИМ уровнем WS-сообщения
    // (`profiler_campaign_stats`), а `ws` здесь — это `wsStats` (= d.stats).
    // Поэтому `ws.profiler_campaign_stats` всегда был undefined, и вкладка
    // «Капча» не получала данных, хотя бэкенд их собирал. Берём из того же
    // стейта, куда обработчик WS и складывает эти статы (booster ∪ profiler).
    const pcsByCid = campaignStats as any
    if (selectedProject && pcsByCid && typeof pcsByCid === 'object' && pcsByCid[selectedProject]) {
      enriched.profilerOkCount = Number(pcsByCid[selectedProject].ok) || 0
      enriched.profilerFailCount = Number(pcsByCid[selectedProject].fail) || 0
      const _cd = captchaDetail[selectedProject] || pcsByCid[selectedProject].captcha_detail
      if (_cd && typeof _cd === 'object') {
        enriched.profilerCaptchaDetail = _cd
      }
    }

    // When a campaign is selected and per-cid data is present, derive
    // counters from THIS campaign's grouped buckets instead of the
    // server-wide `booster_*_count` totals — otherwise the header
    // shows "6562 / 586" for the whole server while the body shows
    // only the selected campaign's items.
    if (selectedProject && okByCid && typeof okByCid === 'object') {
      const sumGroup = (g: any) => {
        if (!g || typeof g !== 'object') return 0
        let n = 0
        for (const k in g) n += Number((g[k] && g[k].count) || 0)
        return n
      }
      enriched.boosterOkCount = sumGroup(enriched.okGrouped)
      enriched.boosterFailCount = sumGroup(enriched.failGrouped)
      enriched.boosterTempCount = sumGroup(enriched.tempGrouped)
      enriched.boosterSkipCount = sumGroup(enriched.skipGrouped)  // Idea #69
    } else {
      if (typeof ws.booster_ok_count === 'number') enriched.boosterOkCount = ws.booster_ok_count
      if (typeof ws.booster_fail_count === 'number') enriched.boosterFailCount = ws.booster_fail_count
      if (typeof ws.booster_temp_count === 'number') enriched.boosterTempCount = ws.booster_temp_count
      if (typeof ws.booster_skip_count === 'number') enriched.boosterSkipCount = ws.booster_skip_count
    }

    const wsMsgs: string[] | undefined = ws.success_msgs
    if (wsMsgs && wsMsgs.length > 0 && enriched.successMessages.size === 0) {
      const m = new Map<string, number>()
      for (const msg of wsMsgs) m.set(msg, (m.get(msg) || 0) + 1)
      enriched.successMessages = m
    }
    const wsFailMsgs: string[] | undefined = ws.fail_msgs
    if (wsFailMsgs && wsFailMsgs.length > 0 && enriched.failMessages.size === 0) {
      const m = new Map<string, number>()
      for (const msg of wsFailMsgs) m.set(msg, (m.get(msg) || 0) + 1)
      enriched.failMessages = m
    }
    const wsRecMsgs: string[] | undefined = ws.recovered_msgs
    if (wsRecMsgs && wsRecMsgs.length > 0 && enriched.recoveredMessages.size === 0) {
      const m = new Map<string, number>()
      for (const msg of wsRecMsgs) m.set(msg, (m.get(msg) || 0) + 1)
      enriched.recoveredMessages = m
    }

    return enriched
  }, [accStats, wsStats, selectedProject, campaignStats, captchaDetail])

  // ── Routing ──

  type RouteTarget =
    | string
    | { project: string }
    | { idea: number }
    | { bug: number }
    | { tool: string }
    | { news: number }

  // Префикс /profiler/* — для всех маршрутов профайлера. Пути остаются
  // совместимы со старыми (/projects, /project/:cid) если переход прямой.
  const routeToPath = useCallback((route: RouteTarget): string => {
    const isProfiler = activeProduct === 'profiler'
    const profilerPrefix = isProfiler ? '/profiler' : ''
    if (typeof route === 'object') {
      if ('project' in route) return `${profilerPrefix}/project/${route.project}`
      if ('idea' in route) return `/ideas/${route.idea}`
      if ('bug' in route) return `/bugs/${route.bug}`
      if ('tool' in route) return `/tools/${route.tool}`
    }
    if (typeof route === 'object' && 'news' in route) return `/news/${(route as { news: number }).news}`
    // Маршруты которые меняются в зависимости от продукта.
    const productRoutes: Record<string, string> = {
      monitoring: isProfiler ? '/profiler' : '/',
      overview: isProfiler ? '/profiler' : '/',
      projects: isProfiler ? '/profiler/projects' : '/projects',
      'profiler-projects': '/profiler/projects',
      'profiler-overview': '/profiler',
    }
    if ((route as string) in productRoutes) return productRoutes[route as string]
    // Общие админ/настроечные маршруты — без префикса.
    return ({ settings: '/settings', tools: '/tools', ideas: '/ideas', 'ideas-admin': '/ideas-admin', bugs: '/bugs', 'bugs-admin': '/bugs-admin', 'ai-tickets-admin': '/ai-tickets-admin', licenses: '/licenses', 'work-servers': '/work-servers', 'alarms-admin': '/alarms-admin', 'scenario-editor': '/scenario-editor', events: '/events', deployments: '/deployments', docs: '/documentation', fingerprints: '/fingerprints', 'identifiers-admin': '/identifiers-admin', identifiers: '/identifiers', 'server-orders': '/server-orders', 'server-orders-admin': '/server-orders-admin' } as Record<string, string>)[route as string] || (isProfiler ? '/profiler' : '/')
  }, [activeProduct])

  const pathToRoute = useCallback((path: string): RouteTarget => {
    // /profiler/project/<cid>
    if (path.startsWith('/profiler/project/')) {
      const id = path.slice(18)
      if (id) return { project: id }
    }
    if (path === '/profiler' || path === '/profiler/') return 'profiler-overview'
    if (path === '/profiler/projects') return 'profiler-projects'
    if (path.startsWith('/project/')) {
      const id = path.slice(9)
      if (id) return { project: id }
    }
    if (path.startsWith('/ideas/')) {
      const raw = path.slice(7)
      const id = parseInt(raw, 10)
      if (!Number.isNaN(id) && id > 0) return { idea: id }
    }
    if (path.startsWith('/bugs/')) {
      const raw = path.slice(6)
      const id = parseInt(raw, 10)
      if (!Number.isNaN(id) && id > 0) return { bug: id }
    }
    if (path.startsWith('/tools/')) {
      const slug = path.slice(7).replace(/\/$/, '')
      if (slug) return { tool: slug }
    }
    if (path.startsWith('/news/')) {
      const id = parseInt(path.slice(6), 10)
      if (!Number.isNaN(id) && id > 0) return { news: id }
    }
    return ({ '/projects': 'projects', '/settings': 'settings', '/tools': 'tools', '/ideas': 'ideas', '/ideas-admin': 'ideas-admin', '/bugs': 'bugs', '/bugs-admin': 'bugs-admin', '/ai-tickets-admin': 'ai-tickets-admin', '/licenses': 'licenses', '/work-servers': 'work-servers', '/alarms-admin': 'alarms-admin', '/scenario-editor': 'scenario-editor', '/events': 'events', '/deployments': 'deployments', '/documentation': 'docs', '/fingerprints': 'fingerprints', '/identifiers-admin': 'identifiers-admin', '/identifiers': 'identifiers', '/server-orders': 'server-orders', '/server-orders-admin': 'server-orders-admin' } as Record<string, string>)[path] || 'monitoring'
  }, [])

  const applyRoute = useCallback((to: RouteTarget) => {
    let id = typeof to === 'string' ? to : null
    // Для профайлера 'overview'/'monitoring' должен показывать ProfilerOverview
    if (id === 'overview' || id === 'monitoring') {
      if (activeProduct === 'profiler') id = 'profiler-overview'
    }
    const isIdea = typeof to === 'object' && 'idea' in to
    const isBug = typeof to === 'object' && 'bug' in to
    const isTool = typeof to === 'object' && 'tool' in to
    setShowSettings(id === 'settings')
    setShowLicenses(!IS_BOOSTER_ONLY && id === 'licenses')
    setShowWorkServers(!IS_BOOSTER_ONLY && id === 'work-servers')
    setShowAlarmsAdmin(id === 'alarms-admin')
    setShowScenarioEditor(!IS_BOOSTER_ONLY && id === 'scenario-editor')
    const isNews = typeof to === 'object' && 'news' in to
    setShowEvents(id === 'events' || isNews)
    if (isNews) {
      setSelectedNewsId((to as { news: number }).news)
    } else {
      setSelectedNewsId(null); setSelectedNewsTitle('')
    }
    setShowDeployments(!IS_BOOSTER_ONLY && id === 'deployments')
    setShowDocs(id === 'docs')
    setShowTools(id === 'tools' || isTool)
    setShowIdeas(id === 'ideas' || isIdea)
    setShowIdeasAdmin(id === 'ideas-admin')
    setShowBugs(id === 'bugs' || isBug)
    setShowBugsAdmin(id === 'bugs-admin')
    setShowAiTickets(!IS_BOOSTER_ONLY && id === 'ai-tickets-admin')
    setShowProjects(id === 'projects')
    setShowProfilerProjects(id === 'profiler-projects')
    setShowProfilerOverview(id === 'profiler-overview')
    setShowFingerprints(!IS_BOOSTER_ONLY && id === 'fingerprints')
    setShowIdentifiersAdmin(!IS_BOOSTER_ONLY && id === 'identifiers-admin')
    setShowIdentifiers(id === 'identifiers')
    setShowServerOrders(id === 'server-orders')
    setShowServerOrdersAdmin(id === 'server-orders-admin')
    if (typeof to === 'object' && 'project' in to) {
      setSelectedProject(to.project)
    } else {
      setSelectedProject(null)
    }
    if (isIdea) {
      setSelectedIdeaId((to as { idea: number }).idea)
    } else {
      setSelectedIdeaId(null); setSelectedIdeaTitle('')
    }
    if (isBug) {
      setSelectedBugId((to as { bug: number }).bug)
    } else {
      setSelectedBugId(null); setSelectedBugTitle('')
    }
    if (isTool) {
      setSelectedToolSlug((to as { tool: string }).tool)
    } else {
      setSelectedToolSlug(null)
    }
  }, [activeProduct])

  const navigate = useCallback((to: RouteTarget, replace = false) => {
    applyRoute(to)
    const path = routeToPath(to)
    if (replace) {
      window.history.replaceState({ route: path }, '', path)
    } else {
      window.history.pushState({ route: path }, '', path)
    }
  }, [applyRoute, routeToPath])

  const isOverview = !showSettings && !showLicenses && !showWorkServers && !showAlarmsAdmin && !showScenarioEditor && !showEvents && !showDeployments && !showDocs && !showTools && !showIdeas && !showIdeasAdmin && !showBugs && !showBugsAdmin && !showAiTickets && !showProjects && !showProfilerProjects && !showFingerprints && !showIdentifiersAdmin && !showIdentifiers && !showServerOrders && !showServerOrdersAdmin && !selectedProject

  // ── Data fetching ──

  const refreshProjects = useCallback(() => {
    // Грузим список проектов в зависимости от активного продукта.
    // /api/booster/campaigns возвращает массив (legacy форма),
    // /api/profiler/campaigns возвращает {campaigns: [...]}.
    if (activeProduct === 'profiler') {
      fetch('/api/profiler/campaigns').then(r => r.json()).then((d: any) => setProfilerProjects(d?.campaigns || [])).catch(() => {})
    } else {
      fetch('/api/booster/campaigns').then(r => r.json()).then(setBoosterProjects).catch(() => {})
    }
  }, [activeProduct])

  const loadProject = useCallback(async (projectId: string, pushUrl = true) => {
    // Bug (vVovk #3 / регрессия Bug #8): если повторно открываем УЖЕ
    // выбранный проект, у которого есть несохранённые правки — НЕ
    // перезагружаем конфиг. Иначе ветка «тёплого кэша» ниже синхронно
    // делает setSelectedCfg(cached.cfg)+setIsDirty(false) и молча
    // затирает ввод. Особенно бьёт по клонам: у свежей копии кэш весь
    // сеанс «тёплый» (<60с) и хранит снапшот ИСТОЧНИКА, поэтому любой
    // повторный loadProject (клик в сайдбаре, сохранение модалки
    // переименования) откатывал правки «под новый сайт» → приходилось
    // сохранять каждое поле по отдельности. Ранний выход сохраняет ввод.
    if (projectId === selectedProjectRef.current && isDirtyRef.current) {
      if (pushUrl) navigate({ project: projectId }); else applyRoute({ project: projectId })
      return
    }
    // Profiler-проекты грузятся через /api/profiler/campaigns/{cid}, не
    // через booster API. Файлов у них нет (только settings).
    const isProfiler = activeProduct === 'profiler'
    if (isProfiler) {
      const reqId = ++loadSeqRef.current
      setCfgLoading(true)
      try {
        const data: any = await getProfilerCampaign(projectId)
        // Stale-response guard: if the user switched to another project (or
        // back to the global view) while this request was in flight, do NOT
        // overwrite the now-current cfg. Source of truth stays the project
        // the operator is actually looking at.
        if (loadSeqRef.current !== reqId) return
        // Mirror booster: catalog defaults must be the floor, otherwise
        // Select fields left at their default (e.g. SERVER_REMOTE_STATUS=
        // 'Включено') never land in settings → the scenario reads a blank
        // SERVER_REMOTE_STATUS, falls into local mode and BD_NAME stays the
        // literal [[BD_NAME]] → «нет профилей». Saved values win over defaults.
        const merged: Settings = { ...getProfilerDefaults(), ...(data?.settings || {}) }
        setProfilerCfg(merged)
        if (pushUrl) navigate({ project: projectId }); else applyRoute({ project: projectId })
        setIsDirty(false)
      } catch {
        setAlert({ type: 'error', text: 'Не удалось загрузить настройки проекта' })
      } finally {
        setCfgLoading(false)
      }
      return
    }
    const cached = projectCacheRef.current[projectId]
    const now = Date.now()
    if (cached && now - cached.ts < 60_000) {
      setSelectedCfg(cached.cfg)
      setCampaignFiles(cached.files)
      if (pushUrl) navigate({ project: projectId }); else applyRoute({ project: projectId })
      setIsDirty(false)
      getCampaign(projectId).then(data => {
        const merged: Settings = { ...getBoosterDefaults(), ...(data.settings || {}) }
        // Bug #8: don't overwrite local edits with the background snapshot.
        // - If the user already switched away from this project — late
        //   response is for a different cid, ignore the cfg patch entirely.
        // - If the user has unsaved edits (isDirty) — keep their edits and
        //   only refresh the file list cache. Otherwise the response from
        //   getCampaign races against the user's tab-to-tab edits and wipes
        //   anything they typed in the second tab before clicking Save.
        setSelectedCfg(prev => {
          if (selectedProjectRef.current !== projectId) return prev
          if (isDirtyRef.current) return prev
          return JSON.stringify(prev) !== JSON.stringify(merged) ? merged : prev
        })
        getCampaignFiles(projectId).then(files => {
          setCampaignFiles(files)
          projectCacheRef.current[projectId] = { cfg: merged, files, ts: Date.now() }
        }).catch(() => {})
      }).catch(() => {})
      return
    }
    const boosterReqId = ++loadSeqRef.current
    setCfgLoading(true)
    try {
      const data = await getCampaign(projectId)
      // Stale-response guard (mirror of profiler branch): a slow cold-load
      // must not clobber the cfg of a project the user already switched to.
      if (loadSeqRef.current !== boosterReqId) return
      const merged: Settings = { ...getBoosterDefaults(), ...(data.settings || {}) }
      setSelectedCfg(merged)
      if (pushUrl) navigate({ project: projectId }); else applyRoute({ project: projectId })
      setIsDirty(false)
      getCampaignFiles(projectId).then(files => {
        setCampaignFiles(files)
        projectCacheRef.current[projectId] = { cfg: merged, files, ts: Date.now() }
      }).catch(() => {})
    } catch {
      setAlert({ type: 'error', text: 'Не удалось загрузить настройки' })
    } finally {
      setCfgLoading(false)
    }
  }, [activeProduct, navigate, applyRoute])

  // Keep refs in sync so background loadProject re-fetch sees fresh values.
  useEffect(() => { selectedProjectRef.current = selectedProject }, [selectedProject])
  useEffect(() => { isDirtyRef.current = isDirty }, [isDirty])

  const handleSelectProject = useCallback((id: string) => { loadProject(id) }, [loadProject])

  // Вкладки проектов: свой список на booster/profiler, переживает F5.
  // Save только от projectTabs — иначе при смене продукта в storage
  // чужого продукта улетели бы вкладки предыдущего.
  const projectTabsProductRef = useRef(activeProduct)
  projectTabsProductRef.current = activeProduct
  useEffect(() => {
    setProjectTabs(loadProjectTabs(activeProduct))
  }, [activeProduct])
  useEffect(() => {
    saveProjectTabs(projectTabsProductRef.current, projectTabs)
  }, [projectTabs])

  // Открыли проект → добавить/обновить вкладку.
  useEffect(() => {
    if (!selectedProject) return
    const name =
      projects.find((p) => p.id === selectedProject)?.name ||
      selectedProject
    setProjectTabs((prev) => upsertProjectTab(prev, selectedProject, name))
  }, [selectedProject, projects])

  // Список проектов подгрузился — подтянуть имена и выкинуть удалённые.
  // Если ни одна вкладка не матчит список — не трогаем (гонка со сменой
  // booster↔profiler: projects уже новые, а tabs ещё старые до load).
  useEffect(() => {
    if (!projects.length) return
    const byId = new Map(projects.map((p) => [p.id, p]))
    setProjectTabs((prev) => {
      if (!prev.length) return prev
      if (!prev.some((t) => byId.has(t.id))) return prev
      let changed = false
      const next: ProjectTab[] = []
      for (const t of prev) {
        const p = byId.get(t.id)
        if (!p) {
          changed = true
          continue
        }
        const name = p.name || t.name
        if (name !== t.name) {
          changed = true
          next.push({ id: t.id, name })
        } else {
          next.push(t)
        }
      }
      return changed ? next : prev
    })
  }, [projects])

  const handleCloseProjectTab = useCallback((id: string) => {
    setProjectTabs((prev) => {
      const { tabs, switchTo } = closeProjectTab(prev, id, selectedProject)
      if (selectedProject === id) {
        queueMicrotask(() => {
          if (switchTo) handleSelectProject(switchTo)
          else navigate('overview')
        })
      }
      return tabs
    })
  }, [selectedProject, handleSelectProject, navigate])

  const displayProjectTabs = useMemo(() => {
    if (!projectTabs.length) return projectTabs
    return projectTabs.map((t) => {
      const p = projects.find((x) => x.id === t.id)
      if (!p) return t
      const name = p.name || t.name
      const running = !!(p as any).running
      if (name === t.name && !!t.running === running) return t
      return { id: t.id, name, running }
    })
  }, [projectTabs, projects])

  // ── Route initialization ──

  const routeInitRef = useRef(false)
  useEffect(() => {
    if (routeInitRef.current) return
    routeInitRef.current = true
    // Operator: force redirect to /licenses — no other pages available.
    if (isOperator) {
      applyRoute('licenses')
      window.history.replaceState({ route: '/licenses' }, '', '/licenses')
      return
    }
    const route = pathToRoute(window.location.pathname)
    applyRoute(route)
    window.history.replaceState({ route: window.location.pathname }, '', window.location.pathname)
    if (typeof route === 'object' && 'project' in route) loadProject(route.project, false)

    const onPopState = () => {
      const r = pathToRoute(window.location.pathname)
      applyRoute(r)
      if (typeof r === 'object' && 'project' in r) loadProject(r.project, false)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [pathToRoute, applyRoute, loadProject])

  // ── Persistence ──

  useEffect(() => { localStorage.setItem('cb-panel-mode', activeProduct) }, [activeProduct])
  // Legacy GLOBAL profiler config (settings.json). This is the OLD
  // single-config mode used only when NO profiler project is selected.
  //
  // Bug (профайлер сбрасывал SERVER_REMOTE_STATUS/ID_SERVER_REMOTE): раньше
  // этот эффект грузил глобальный /api/config в `profilerCfg` при ЛЮБОМ входе
  // в профайлер — в т.ч. когда открыт конкретный проект. Он гонялся с
  // loadProject() и затирал per-project настройки значениями из глобального
  // settings.json (там SERVER_REMOTE_STATUS='Отключено'). Затем Save/Start
  // (handleSaveConfig / handleProfilerStart при isDirty) сохранял эти
  // подменённые значения в конфиг проекта → удалённый режим «сбрасывался» на
  // локальный. У бустера такого глобального загрузчика нет, поэтому он
  // настройки не терял. Теперь грузим глобальный конфиг ТОЛЬКО когда проект
  // не выбран, и перепроверяем ref в .then, чтобы не затереть проект, если
  // его выбрали пока запрос был в полёте.
  useEffect(() => {
    if (activeProduct !== 'profiler') return
    if (selectedProject) return
    getProfilerConfig().then(data => {
      if (selectedProjectRef.current) return
      setProfilerCfg(data || {})
    }).catch(() => {})
  }, [activeProduct, selectedProject])
  useEffect(() => {
    if (selectedProject) localStorage.setItem('cb-active-project', selectedProject)
    else localStorage.removeItem('cb-active-project')
  }, [selectedProject])
  useEffect(() => { localStorage.setItem('cb-booster-right-tab', rightTab) }, [rightTab])

  useEffect(() => {
    if (activeProduct === 'profiler') {
      const visible = getVisibleProfilerSections(profilerCfg, isSuperAdmin)
      if (visible.length > 0 && !visible.includes(selectedSection)) setSelectedSection(visible[0])
    } else {
      const visible = getVisibleBoosterSections(selectedCfg)
      if (visible.length > 0 && !visible.includes(selectedSection as BoosterSectionName)) setSelectedSection(visible[0])
    }
  }, [activeProduct, profilerCfg, selectedCfg, selectedSection, isSuperAdmin])

  // ── WebSocket ──

  // ── Page Visibility Sleep Mode ──────────────────────────────────
  // Оптимизация памяти и CPU: если вкладка скрыта > SLEEP_AFTER_MS
  // (10 мин), переходим в «сон» — останавливаем dashboard-init polling,
  // буферизуем WS state/slow обновления (не вызываем setState → нет
  // лишних React перерисовок). При возврате < 10 мин — всё продолжает
  // работать без задержки. При возврате > 10 мин — один мгновенный
  // dashboard-init и применяем накопленный WS state.
  // WS соединение никогда не закрываем — логи воркеров копятся в буфере.
  const SLEEP_AFTER_MS = 10 * 60 * 1000  // 10 минут
  // Live in-memory log buffer cap (per browser tab). This is the "hot"
  // window scrollable instantly; deeper history is pulled on demand from
  // logs.db by LogPanel (scroll-up). Raised 2000 → 5000 so a single project
  // keeps more live history even when several projects log concurrently
  // (the buffer is shared and filtered per-project). ~0.5 KB/line → ~2.5 MB.
  const MAX_LIVE_LOGS = 5000
  const [tabSleeping, setTabSleeping] = useState(false)
  const tabHiddenAtRef = useRef<number | null>(null)
  const sleepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Буферы для накопления пока вкладка в sleep-mode
  const pendingStateRef = useRef<any>(null)
  const pendingStateSlowRef = useRef<any>(null)
  const pendingLogsRef = useRef<any[]>([])

  const applyPendingData = useCallback(() => {
    // Применяем всё накопленное за время сна
    if (pendingStateRef.current) {
      const d = pendingStateRef.current
      setWsState(d.status || {})
      setWsStats(d.stats || {})
      setBoosterProjects(d.campaigns || [])
      setProfilerProjects(d.profiler_campaigns || [])
      if (d.campaign_stats || d.profiler_campaign_stats)
        setCampaignStats({ ...(d.campaign_stats || {}), ...(d.profiler_campaign_stats || {}) })
      if (d.vpn !== undefined) setVpnStatus(d.vpn)
      if (d.available_update !== undefined) setAvailableUpdate(d.available_update ?? null)
      if (d.status?.running !== undefined) setProfilerRunning(!!d.status.running)
      pendingStateRef.current = null
    }
    if (pendingStateSlowRef.current) {
      const d = pendingStateSlowRef.current
      if (d.timeline?.length) setTimeline(d.timeline)
      if (d.hourly_history?.length) setHourlyHistory(d.hourly_history)
      if (d.hourly_campaign_history?.length) setHourlyCampaignHistory(d.hourly_campaign_history)
      if (d.sysinfo) setSysInfo(d.sysinfo)
      pendingStateSlowRef.current = null
    }
    if (pendingLogsRef.current.length > 0) {
      const entries = pendingLogsRef.current
      pendingLogsRef.current = []
      const MAX_ALL = MAX_LIVE_LOGS
      const prevAll = allLogsRef.current
      const combined = prevAll.length + entries.length > MAX_ALL
        ? [...prevAll.slice(-(MAX_ALL - entries.length)), ...entries]
        : [...prevAll, ...entries]
      allLogsRef.current = combined
      setLogs(prev => {
        const next = [...prev, ...entries]
        return next.length > MAX_LIVE_LOGS ? next.slice(-MAX_LIVE_LOGS) : next
      })
      ingestLogs(entries)
    }
  }, [ingestLogs])

  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        tabHiddenAtRef.current = Date.now()
        // Через 10 минут входим в sleep
        sleepTimerRef.current = setTimeout(() => {
          setTabSleeping(true)
        }, SLEEP_AFTER_MS)
      } else {
        // Вкладка снова видна
        if (sleepTimerRef.current) {
          clearTimeout(sleepTimerRef.current)
          sleepTimerRef.current = null
        }
        const wasHiddenFor = tabHiddenAtRef.current ? Date.now() - tabHiddenAtRef.current : 0
        tabHiddenAtRef.current = null

        if (tabSleeping) {
          // Просыпаемся: применяем накопленные данные
          setTabSleeping(false)
          applyPendingData()
        }
        // Если были долго (> 2 мин) — принудительный dashboard-init refresh
        // чтобы badges и счётчики были свежими
        if (wasHiddenFor > 2 * 60 * 1000) {
          fetch('/api/dashboard-init').then(r => r.json()).then(d => {
            const ideasVoting: any[] = Array.isArray(d?.ideas_voting?.ideas) ? d.ideas_voting.ideas : []
            setIdeasVotingCount(ideasVoting.length)
            const ideasAll: any[] = Array.isArray(d?.ideas_all?.ideas) ? d.ideas_all.ideas : []
            const seenIdeas = parseFloat(localStorage.getItem('ideas-last-seen-ts') || '0') || 0
            setIdeasUnreadReplies(ideasAll.filter(it => Number(it.last_admin_reply_at || 0) > seenIdeas).length)
            const bugsNew: any[] = Array.isArray(d?.bugs_new?.bugs) ? d.bugs_new.bugs : []
            setBugsNewCount(bugsNew.length)
            const bugsAll: any[] = Array.isArray(d?.bugs_all?.bugs) ? d.bugs_all.bugs : []
            const seenBugs = parseFloat(localStorage.getItem('bugs-last-seen-ts') || '0') || 0
            setBugsUnreadReplies(bugsAll.filter(it => Number(it.last_admin_reply_at || 0) > seenBugs).length)
            const newsArr: any[] = Array.isArray(d?.news?.news) ? d.news.news : []
            const notifArr: any[] = Array.isArray(d?.notifications?.notifications) ? d.notifications.notifications : []
            setEventsUnread(newsArr.filter(n => !n.read).length + notifArr.filter(n => !n.read).length)
            const srvList = Array.isArray(d?.my_servers?.servers) ? d.my_servers.servers : []
            if (srvList.length > 0) setMyServersList(srvList)
          }).catch(() => {})
        }
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current)
    }
  }, [tabSleeping, applyPendingData])

  const connectWs = useCallback(() => {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const ws = new WebSocket(`${proto}://${location.host}/ws/logs`)
    wsRef.current = ws
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data)
        if (msg.t === 'logs') {
          const entries = msg.d as LogEntry[]
          // allLogsRef: ограничиваем до MAX_LIVE_LOGS последних записей чтобы не
          // допустить memory leak. Раньше рос неограниченно → 881MB+ в
          // Safari через несколько часов → pressure → перестают грузиться
          // другие страницы браузера.
          const MAX_ALL = MAX_LIVE_LOGS
          const prevAll = allLogsRef.current
          const combined = prevAll.length + entries.length > MAX_ALL
            ? [...prevAll.slice(-(MAX_ALL - entries.length)), ...entries]
            : [...prevAll, ...entries]
          allLogsRef.current = combined

          if (tabSleeping) {
            // В sleep-режиме: буферизуем логи, не вызываем setState
            const buf = pendingLogsRef.current
            const merged = [...buf, ...entries]
            pendingLogsRef.current = merged.length > MAX_LIVE_LOGS ? merged.slice(-MAX_LIVE_LOGS) : merged
            return
          }
          setLogs(prev => {
            const next = [...prev, ...entries]
            // Ring-buffer: оставляем последние MAX_LIVE_LOGS строк
            return next.length > MAX_LIVE_LOGS ? next.slice(-MAX_LIVE_LOGS) : next
          })
          ingestLogs(entries)
        } else if (msg.t === 'notification_push') {
          const item = msg.d as LivePushNotification
          if (item && item.id && !livePushDismissedRef.current.has(item.id)) {
            // Уведомления применяем всегда — они важны даже в sleep
            setLivePushQueue(prev => {
              if (prev.some(p => p.id === item.id)) return prev
              return [...prev, item]
            })
            setEventsUnread(c => c + 1)
          }
        } else if (msg.t === 'state') {
          const d = msg.d
          if (tabSleeping) {
            // Sleep: сохраняем только последний state — старый не нужен
            pendingStateRef.current = d
            return
          }
          setWsState(d.status || {})
          setWsStats(d.stats || {})
          setBoosterProjects(d.campaigns || [])
          setProfilerProjects(d.profiler_campaigns || [])
          // timeline/hourly/campaign_stats теперь шлются только в state_slow
          // (раз в 60с), не в каждом fast-тике state. Но если поля есть
          // (например на старом сервере или первый WS connect) — применяем.
          if (d.timeline?.length) setTimeline(d.timeline)
          if (d.hourly_history?.length) setHourlyHistory(d.hourly_history)
          if (d.hourly_campaign_history?.length) setHourlyCampaignHistory(d.hourly_campaign_history)
          if (d.campaign_stats || d.profiler_campaign_stats)
            setCampaignStats({ ...(d.campaign_stats || {}), ...(d.profiler_campaign_stats || {}) })
          if (d.sysinfo) setSysInfo(d.sysinfo)
          if (d.vpn !== undefined) setVpnStatus(d.vpn)
          if (d.available_update !== undefined) setAvailableUpdate(d.available_update ?? null)
          if (d.status?.running !== undefined) setProfilerRunning(!!d.status.running)
          // Per-project profiler running
          if (activeProduct === 'profiler' && selectedProject && d.status?.campaigns) {
            const pcState = (d.status.campaigns as any)?.[selectedProject]
            if (pcState !== undefined) {
              setProfilerRunning(!!pcState.running)
            }
          }
          // Воркеры — обновляются в fast state (каждые 3с)
          if (d.status?.campaigns) {
            const workers: any[] = []
            for (const [cid, cState] of Object.entries(d.status.campaigns) as any) {
              if (cState.running && cState.workers) {
                for (const w of cState.workers) workers.push({ ...w, _campaign_id: cid, _campaign: cState.campaign_name })
              }
            }
            setAllWorkers(workers)
          }
        } else if (msg.t === 'state_slow') {
          // Heavy-данные раз в 60с — ТОЛЬКО то что меняется раз в минуту:
          // timeline, hourly_history, hourly_campaign_history, sysinfo.
          // campaign_stats (ok/fail реалтайм) — в fast state, не здесь.
          const d = msg.d
          if (tabSleeping) {
            pendingStateSlowRef.current = d
            return
          }
          if (d.timeline?.length) setTimeline(d.timeline)
          // Bug #178: применяем hourly_history всегда если поле есть в payload,
          // даже если пустое (d.hourly_history === []) — иначе при отсутствии
          // данных за день старые данные остаются на графике.
          if ('hourly_history' in d) setHourlyHistory(d.hourly_history || [])
          if ('hourly_campaign_history' in d) setHourlyCampaignHistory(d.hourly_campaign_history || [])
          if (d.sysinfo) setSysInfo(d.sysinfo)
          // Аналитика капчи (вкладка «Капча») — тяжёлый разрез, приходит раз
          // в минуту отдельно от реалтайм-счётчиков.
          if (d.profiler_captcha_detail) setCaptchaDetail(d.profiler_captcha_detail)
          if (d.profiler_pool_stats) setPoolStats(d.profiler_pool_stats)
        }
      } catch { /* ignore */ }
    }
    ws.onclose = () => { clearTimeout(reconnectTimer.current); reconnectTimer.current = setTimeout(connectWs, 3000) }
    ws.onerror = () => ws.close()
  }, [ingestLogs])

  useEffect(() => {
    connectWs()
    return () => { clearTimeout(reconnectTimer.current); wsRef.current?.close() }
  }, [connectWs])

  // ── Initial data ──

  useEffect(() => {
    refreshProjects()
    fetch('/api/me').then(r => r.json()).then(info => {
      if (info.server_name) { setServerName(info.server_name); localStorage.setItem('cb-server-name', info.server_name) }
      setHasInternalDocs(!!info._internal_docs)
    }).catch(() => {})
  }, [refreshProjects])

  // Periodic poll of ideas count (voting status) + unread admin replies
  // for the sidebar badge.
  // dashboard-init: один запрос вместо 8 отдельных к лицсерверу.
  // Раньше при монте панели летело: ideas?status=voting, ideas?limit=200,
  // bugs?statuses=new,confirmed, bugs?limit=200, news, notifications,
  // my-servers, yandex-updates — каждый отдельный HTTP round-trip к
  // лицсерверу через WAN. Теперь — 1 запрос, все данные параллельно на
  // сервере, ответ приходит за max(individual) вместо sum(individual).
  useEffect(() => {
    let alive = true
    const loadAll = () => {
      // В sleep-режиме не делаем лишние запросы — данные обновятся при пробуждении
      if (tabSleeping) return
      fetch('/api/dashboard-init').then(r => r.json()).then(d => {
        if (!alive) return

        // ideas voting count + unread replies
        const ideasVoting: any[] = Array.isArray(d?.ideas_voting?.ideas) ? d.ideas_voting.ideas : []
        setIdeasVotingCount(ideasVoting.length)
        const ideasAll: any[] = Array.isArray(d?.ideas_all?.ideas) ? d.ideas_all.ideas : []
        const seenIdeas = parseFloat(localStorage.getItem('ideas-last-seen-ts') || '0') || 0
        setIdeasUnreadReplies(ideasAll.filter(it => Number(it.last_admin_reply_at || 0) > seenIdeas).length)

        // bugs count + unread replies
        const bugsNew: any[] = Array.isArray(d?.bugs_new?.bugs) ? d.bugs_new.bugs : []
        setBugsNewCount(bugsNew.length)
        const bugsAll: any[] = Array.isArray(d?.bugs_all?.bugs) ? d.bugs_all.bugs : []
        const seenBugs = parseFloat(localStorage.getItem('bugs-last-seen-ts') || '0') || 0
        setBugsUnreadReplies(bugsAll.filter(it => Number(it.last_admin_reply_at || 0) > seenBugs).length)

        // news + notifications unread badge
        const newsArr: any[] = Array.isArray(d?.news?.news) ? d.news.news : []
        const notifArr: any[] = Array.isArray(d?.notifications?.notifications) ? d.notifications.notifications : []
        setEventsUnread(newsArr.filter(n => !n.read).length + notifArr.filter(n => !n.read).length)

        // my-servers — сохраняем в state чтобы ServerSwitcher не делал отдельный fetch
        const srvList = Array.isArray(d?.my_servers?.servers) ? d.my_servers.servers : []
        if (srvList.length > 0) setMyServersList(srvList)
      }).catch(() => {})
    }
    loadAll()
    // refresh каждые 60с (вместо 3 отдельных интервалов 60s/90s/90s)
    const t = setInterval(loadAll, 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  // Mark current section as "seen" whenever the user opens it. The next
  // poll cycle will reset the unread badge to 0 automatically.
  useEffect(() => {
    if (showBugs) {
      localStorage.setItem('bugs-last-seen-ts', String(Math.floor(Date.now() / 1000)))
      setBugsUnreadReplies(0)
    }
  }, [showBugs])
  useEffect(() => {
    if (showIdeas) {
      localStorage.setItem('ideas-last-seen-ts', String(Math.floor(Date.now() / 1000)))
      setIdeasUnreadReplies(0)
    }
  }, [showIdeas])

  // Разрез капчи приходит по WS только раз в минуту, поэтому после
  // перезагрузки страницы вкладка была бы пустой до следующей рассылки.
  // Подтягиваем накопленное с сервера сразу при выборе проекта.
  useEffect(() => {
    if (activeProduct !== 'profiler' || !selectedProject) return
    const cid = selectedProject
    let cancelled = false
    getProfilerCaptchaDetail(cid)
      .then((d: any) => {
        if (cancelled || !d || !d.captcha_detail) return
        setCaptchaDetail(prev => (
          Object.keys(d.captcha_detail).length ? { ...prev, [cid]: d.captcha_detail } : prev
        ))
      })
      .catch(() => { /* вкладка просто дождётся рассылки по WS */ })
    return () => { cancelled = true }
  }, [activeProduct, selectedProject])

  const selectedProjectName = useMemo(() =>
    projects.find(c => c.id === selectedProject)?.name || '',
  [projects, selectedProject])

  const TOOL_TITLES: Record<string, string> = {
    'proxy-checker': 'Прокси-чекер',
    'vpn-setup': 'VPN на сервер',
    'yandex-updates': 'Апдейты Яндекса',
  }

  useEffect(() => {
    const brandSuffix = (serverName || 'BOOSTER PANEL') + ' — CR33P BOOSTER'
    let page = ''
    if (showSettings) page = 'Настройки панели'
    else if (showTools && selectedToolSlug) page = `${TOOL_TITLES[selectedToolSlug] || selectedToolSlug} · Инструменты`
    else if (showTools) page = 'Инструменты'
    else if (showIdeas && selectedIdeaId && selectedIdeaTitle) page = `${selectedIdeaTitle} · Идеи`
    else if (showIdeas && selectedIdeaId) page = `Идея #${selectedIdeaId}`
    else if (showIdeas) page = 'Идеи и предложения'
    else if (showIdeasAdmin) page = 'Идеи (модерация)'
    else if (showBugs && selectedBugId && selectedBugTitle) page = `${selectedBugTitle} · Баги`
    else if (showBugs && selectedBugId) page = `Баг #${selectedBugId}`
    else if (showBugs) page = 'Баги и ошибки'
    else if (showBugsAdmin) page = 'Баги (модерация)'
    else if (showAiTickets) page = 'AI-тикеты'
    else if (showDocs) page = 'Документация'
    else if (showLicenses) page = 'Лицензии'
    else if (showWorkServers) page = 'Рабочие серверы'
    else if (showAlarmsAdmin) page = 'Новости и уведомления'
    else if (showScenarioEditor) page = 'Редактор сценария · Профайлер'
    else if (showEvents && selectedNewsId && selectedNewsTitle) page = `${selectedNewsTitle} · Важные события`
    else if (showEvents && selectedNewsId) page = 'Важные события'
    else if (showEvents) page = 'Важные события'
    else if (showDeployments) page = 'Деплой'
    else if (showFingerprints) page = 'Отпечатки'
    else if (showIdentifiersAdmin) page = 'Управление идентификаторами'
    else if (showIdentifiers) page = 'Идентификаторы'
    else if (showServerOrders) page = 'Предзаказ серверов'
    else if (showServerOrdersAdmin) page = 'Аренда серверов'
    else if (showProjects) page = 'Мои проекты'
    else if (selectedProject && selectedProjectName) page = selectedProjectName
    else page = 'Рабочий стол'
    document.title = `${page} | ${brandSuffix}`
  }, [serverName, showSettings, showTools, selectedToolSlug, showIdeas, selectedIdeaId, selectedIdeaTitle, showIdeasAdmin, showBugs, selectedBugId, selectedBugTitle, showBugsAdmin, showAiTickets, showDocs, showLicenses, showWorkServers, showAlarmsAdmin, showScenarioEditor, showEvents, selectedNewsId, selectedNewsTitle, showDeployments, showFingerprints, showIdentifiersAdmin, showIdentifiers, showServerOrders, showServerOrdersAdmin, showProjects, selectedProject, selectedProjectName])

  // ── Alert auto-dismiss ──

  useEffect(() => {
    if (!alert) return
    const t = setTimeout(() => setAlert(null), 4000)
    return () => clearTimeout(t)
  }, [alert])

  // ── Config update & save ──

  const handleCfgUpdate = useCallback((patch: Partial<Settings>) => {
    const setter = activeProduct === 'profiler' ? setProfilerCfg : setSelectedCfg
    setter(prev => {
      const next: Settings = {}
      for (const k of Object.keys(prev)) next[k] = prev[k]
      for (const k of Object.keys(patch)) if (patch[k] !== undefined) next[k] = patch[k]!
      return next
    })
    setIsDirty(true)
  }, [activeProduct])

  // Bug #8: prevent overlapping saves. Without this, a user spamming the
  // Save button (e.g. they thought save was per-tab and clicked 6 times)
  // queued 6 parallel PUT requests, each of which used to block the
  // panel's event loop on synchronous JSON write — the panel froze for
  // the duration. Now the button is disabled while a save is in flight.
  const [isSaving, setIsSaving] = useState(false)
  const handleSaveConfig = useCallback(async () => {
    if (isSaving) return
    setIsSaving(true)
    try {
      if (activeProduct === 'profiler') {
        // Если выбран конкретный проект профайлера — сохраняем в его пресет,
        // иначе — в глобальный settings.json (legacy путь).
        if (selectedProject) {
          await updateProfilerCampaign(selectedProject, { settings: profilerCfg })
        } else {
          await saveProfilerConfig(profilerCfg)
        }
      } else if (selectedProject) {
        await updateCampaign(selectedProject, { settings: selectedCfg })
        delete projectCacheRef.current[selectedProject]
      }
      setIsDirty(false)
      setAlert({ type: 'success', text: 'Сохранено' })
    } finally {
      setIsSaving(false)
    }
  }, [activeProduct, profilerCfg, selectedCfg, selectedProject, isSaving])

  // ── Campaign actions ──

  const MIN_ACTION_MS = 1200
  const withMinDelay = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    const t0 = Date.now()
    const result = await fn()
    const elapsed = Date.now() - t0
    if (elapsed < MIN_ACTION_MS) await new Promise(r => setTimeout(r, MIN_ACTION_MS - elapsed))
    return result
  }, [])

  const handleStart = useCallback(async () => {
    if (!selectedProject || actionInProgress) return
    setActionInProgress('start')
    setLogs(prev => prev.filter(e => e.campaign_id !== selectedProject))
    allLogsRef.current = allLogsRef.current.filter(e => e.campaign_id !== selectedProject)
    resetStats()
    try {
      // Bug #198: сохраняем настройки ВСЕГДА перед стартом (не только при
      // isDirty). У свежесозданного проекта selectedCfg показывает значения
      // (в т.ч. CNT_THREADS), но isDirty=false — поэтому раньше старт
      // пропускал сохранение и запускал старое число потоков, хотя на
      // экране отображалось другое. Теперь «что на экране — то и стартует».
      await updateCampaign(selectedProject, { settings: selectedCfg }); setIsDirty(false)
      const res = await withMinDelay(() => startCampaign(selectedProject))
      if (!res.error) {
        const name = projects.find(c => c.id === selectedProject)?.name || 'Проект'
        setAlert({ type: 'success', text: `${name} — запущен` })
        setTimeout(refreshProjects, 1000)
      } else {
        setAlert({ type: 'error', text: res.error })
      }
    } finally { setActionInProgress(null) }
  }, [selectedProject, selectedCfg, isDirty, projects, refreshProjects, actionInProgress, withMinDelay])

  const handleStop = useCallback(async () => {
    if (!selectedProject || actionInProgress) return
    // Idea #10: prevent accidental clicks. Switched from window.confirm
    // to a styled AlertDialog so the dialog matches the rest of the
    // panel's design (operator request).
    const projName = projects.find(c => c.id === selectedProject)?.name || 'проект'
    if (!await appConfirm({
      title: `Остановить «${projName}»?`,
      description: 'Все активные воркеры будут немедленно завершены.',
      confirmText: 'Остановить',
      destructive: true,
    })) return
    setActionInProgress('stop')
    try {
      if (isDirty) { await updateCampaign(selectedProject, { settings: selectedCfg }); setIsDirty(false) }
      await withMinDelay(() => stopCampaign(selectedProject))
      setAlert({ type: 'success', text: `${projName} — остановлен` })
      setRightTab('settings')
      setTimeout(refreshProjects, 1000)
    } finally { setActionInProgress(null) }
  }, [selectedProject, selectedCfg, isDirty, projects, refreshProjects, actionInProgress, withMinDelay])

  const handlePause = useCallback(async () => {
    if (!selectedProject || actionInProgress) return
    setActionInProgress('stop')
    try {
      await withMinDelay(() => pauseCampaign(selectedProject))
      const name = projects.find(c => c.id === selectedProject)?.name || 'Проект'
      setAlert({ type: 'success', text: `${name} — на паузе` })
      setTimeout(refreshProjects, 1000)
    } finally { setActionInProgress(null) }
  }, [selectedProject, projects, refreshProjects, actionInProgress, withMinDelay])

  const handleResume = useCallback(async () => {
    if (!selectedProject || actionInProgress) return
    setActionInProgress('start')
    try {
      // Bug #198: безусловное сохранение перед возобновлением (см. handleStart).
      await updateCampaign(selectedProject, { settings: selectedCfg }); setIsDirty(false)
      await withMinDelay(() => resumeCampaign(selectedProject))
      const name = projects.find(c => c.id === selectedProject)?.name || 'Проект'
      setAlert({ type: 'success', text: `${name} — возобновлён` })
      setTimeout(refreshProjects, 1000)
    } finally { setActionInProgress(null) }
  }, [selectedProject, selectedCfg, isDirty, projects, refreshProjects, actionInProgress, withMinDelay])

  const handleRestart = useCallback(async () => {
    if (!selectedProject || actionInProgress) return
    const name = projects.find(c => c.id === selectedProject)?.name || 'проект'
    // Idea #10: confirm before destructive action (styled dialog).
    if (!await appConfirm({
      title: `Перезапустить «${name}»?`,
      description: 'Текущие потоки будут остановлены и запущены заново.',
      confirmText: 'Перезапустить',
    })) return
    setActionInProgress('stop')
    try {
      await stopCampaign(selectedProject)
      setAlert({ type: 'success', text: `${name} — перезапуск...` })
      setTimeout(async () => {
        try {
          // Bug #198: безусловное сохранение перед перезапуском (см. handleStart).
          await updateCampaign(selectedProject, { settings: selectedCfg }); setIsDirty(false)
          await startCampaign(selectedProject)
          setAlert({ type: 'success', text: `${name} — запущен` })
          setTimeout(refreshProjects, 1000)
        } catch { setAlert({ type: 'error', text: 'Ошибка при перезапуске' }) }
        setActionInProgress(null)
      }, 2500)
    } catch { setActionInProgress(null) }
  }, [selectedProject, selectedCfg, isDirty, projects, refreshProjects, actionInProgress])

  const handleProfilerStart = useCallback(async () => {
    setActionInProgress('start')
    try {
      // Если открыт конкретный проект профайлера — стартуем его, иначе глобально.
      if (selectedProject) {
        if (isDirty) {
          await updateProfilerCampaign(selectedProject, { settings: profilerCfg })
          setIsDirty(false)
        }
        await startProfilerCampaign(selectedProject)
        setProfilerRunning(true)
        setAlert({ type: 'success', text: 'Profiler — запущен' })
      } else {
        if (isDirty) { await saveProfilerConfig(profilerCfg); setIsDirty(false) }
        const res = await startProfiler()
        if (!res.error) {
          setProfilerRunning(true)
          setAlert({ type: 'success', text: 'Profiler — запущен' })
        } else {
          setAlert({ type: 'error', text: res.error })
        }
      }
    } catch (e: any) {
      setAlert({ type: 'error', text: 'Запуск не удался: ' + (e?.message || e) })
    } finally { setActionInProgress(null) }
  }, [profilerCfg, isDirty, selectedProject])

  const handleProfilerStop = useCallback(async () => {
    setActionInProgress('stop')
    try {
      if (selectedProject) {
        await stopProfilerCampaign(selectedProject)
      } else {
        await stopProfiler()
      }
      setProfilerRunning(false)
      setAlert({ type: 'success', text: 'Profiler — остановлен' })
    } finally { setActionInProgress(null) }
  }, [selectedProject])

  const handleDeleteProject = useCallback((id: string) => { setDeleteProjectId(id) }, [])

  const handleEditProject = useCallback(async (projectId: string) => {
    try {
      const data = await getCampaign(projectId)
      const cfg = data.settings || {}
      setEditingProject({
        id: data.id, name: data.name, comment: data.comment, color: data.color || '',
        archived: data.archived, created_at: data.created_at, running: data.running,
        sched_enabled: cfg.SCHED_ENABLED === 'Да', sched_start_time: cfg.SCHED_START_TIME || '',
        // Bug #134: ProjectModal → SchedulerDialog нужны ВСЕ поля
        // расписания, иначе при сохранении edit-modal'а тип ('continuous'/
        // 'interval' и т.д.), work_from/work_to, days и интервалы
        // сбрасываются в дефолт ('time' + 09:00) — клиент теряет
        // настроенное непрерывное расписание после первого же
        // редактирования имени/комментария.
        sched_type: cfg.SCHED_TYPE || 'time',
        sched_days: cfg.SCHED_DAYS || 'Каждый день',
        sched_days_custom: cfg.SCHED_DAYS_CUSTOM || '',
        sched_work_from: cfg.SCHED_WORK_FROM || '00:00',
        sched_work_to: cfg.SCHED_WORK_TO || '23:59',
        sched_run_hours: Number(cfg.SCHED_RUN_HOURS) || 0,
        sched_run_minutes: Number(cfg.SCHED_RUN_MINUTES) || 0,
        sched_interval_minutes: Number(cfg.SCHED_INTERVAL_MINUTES) || 10,
        sched_restart_max: Number(cfg.SCHED_RESTART_MAX) || 0,
        sched_restart_delay: Number(cfg.SCHED_RESTART_DELAY) || 5,
        sched_hourly_minute: Number(cfg.SCHED_HOURLY_MINUTE) || 0,
        // Bug #193: «Случайное время старта» (Idea #65) терялось при
        // редактировании проекта через ProjectModal — эти два поля не
        // прокидывались в initial.schedule, поэтому чекбокс показывался
        // выключенным, а сохранение через этот путь могло сбросить
        // SCHED_START_RANDOM в «Нет». Грузим их наравне с остальными.
        sched_start_random: cfg.SCHED_START_RANDOM === 'Да',
        sched_start_time_to: cfg.SCHED_START_TIME_TO || '',
      })
      setEditingProjectId(projectId)
    } catch { /* ignore */ }
  }, [])

  const handleInstallUpdate = useCallback(async () => {
    if (!confirm('Панель перезапустится для установки обновления. Продолжить?')) return
    setIsUpdating(true)
    await fetch('/api/update/install', { method: 'POST' })
  }, [])

  // ── Derived state ──

  const currentCampaign = selectedProject ? projects.find(p => p.id === selectedProject) : null
  const campaignRunning = !!(currentCampaign as any)?.running
  const campaignPaused = !!(currentCampaign as any)?.paused
  const activeCfg = activeProduct === 'profiler' ? profilerCfg : selectedCfg
  const visibleSections = (activeProduct === 'profiler'
    ? getVisibleProfilerSections(profilerCfg, isSuperAdmin) as (BoosterSectionName | ProfilerSectionName)[]
    : selectedProject ? getVisibleBoosterSections(selectedCfg) : []
  ).filter(s => (s !== 'Баг-Чекер' && s !== 'Уведомления Telegram') || isSuperAdmin)
  const anyRunning = projects.some(c => (c as any).running)
  const activeProjects = useMemo(() => projects.filter(p => !p.archived), [projects])
  const archivedProjects = useMemo(() => projects.filter(p => p.archived), [projects])

  // Bug #48/#119: фильтрация логов по проекту — для ОБОИХ продуктов.
  // Ранее для profiler фильтрация отключалась (показывало все логи всех
  // кампаний), что приводило к «тикают чужие логи в незапущенном проекте».
  const filteredLogs = useMemo(() =>
    !selectedProject ? logs : logs.filter(e => e.campaign_id === selectedProject),
  [logs, selectedProject])

  const getAllLogs = useCallback(() => {
    const all = allLogsRef.current
    if (!selectedProject) return all
    return all.filter(e => e.campaign_id === selectedProject)
  }, [selectedProject])

  const projectWorkers = useMemo(() => {
    // Раньше у профайлера был один глобальный конфиг, и здесь показывали ВСЕХ
    // воркеров панели без фильтра. Сейчас у профайлера свои проекты, поэтому
    // такой список подмешивал в карточку чужие потоки — у остановленного
    // проекта висели работающие воркеры соседней (в т.ч. бустерной) кампании.
    if (!selectedProject) return []
    return allWorkers.filter(w => w._campaign_id === selectedProject)
  }, [activeProduct, allWorkers, selectedProject])



  const resultStats = useMemo(() => {
    const camp = currentCampaign as any
    const cs = selectedProject ? campaignStats[selectedProject] : null
    const ok = (cs as any)?.ok ?? camp?.run_ok ?? 0
    const fail = (cs as any)?.fail ?? camp?.run_fail ?? 0
    const captcha = (cs as any)?.captcha ?? camp?.run_captcha ?? 0
    const avgPos = (cs as any)?.avg_position ?? camp?.run_avg_pos ?? 0
    const error429 = (cs as any)?.error_429 ?? 0
    const elapsed = camp?.started_at ? Math.floor(Date.now() / 1000 - camp.started_at) : 0
    // Скорость «ok/час» считаем от durable rate-anchor (rate_since), а НЕ от
    // per-card started_at: последний намеренно сбрасывается на деплое/рестарте
    // (uptime, Bug #208), из-за чего ok/(почти-ноль) давал дикий спайк скорости
    // сразу после деплоя. rate_since переживает рестарт (campaign_runtime) →
    // скорость продолжается «как было».
    const rateSince = (cs as any)?.rate_since ?? (camp as any)?.rate_since ?? 0
    const rateElapsed = rateSince ? Math.floor(Date.now() / 1000 - rateSince) : elapsed
    const speed = rateElapsed > 0 && ok > 0 ? Math.round(ok / (rateElapsed / 3600)) : (cs as any)?.speed_per_hour ?? 0
    return {
      ok, fail, captcha, avg_position: avgPos,
      speed_per_hour: speed, elapsed_sec: elapsed,
      captcha_types: (cs as any)?.captcha_types || {},
      // Число САМИХ капч (первичные обнаружения). Разрез captcha_types —
      // это ЭТАПЫ одной капчи (чекбокс → картинка), их сумма ≠ числу капч.
      captcha_events: (cs as any)?.captcha_events ?? 0,
      error_429: error429,
      ok_agg: (cs as any)?.ok_agg, fail_agg: (cs as any)?.fail_agg,
      total_tasks: (cs as any)?.total_tasks ?? 0,
      remaining_tasks: (cs as any)?.remaining_tasks ?? 0,
      // Profiler per-iteration timing (avg/min/max seconds) — 0 for booster.
      iter_avg_sec: (cs as any)?.iter_avg_sec ?? 0,
      iter_min_sec: (cs as any)?.iter_min_sec ?? 0,
      iter_max_sec: (cs as any)?.iter_max_sec ?? 0,
      // Разбивка времени итерации по типам кубиков (сумма + число итераций,
      // средние считает сама панель) — источник подсказки «на что уходит время».
      phase_stats: (cs as any)?.phase_stats,
      // Тренд за сутки для спарклайна: берём УЖЕ загруженную почасовую историю
      // этого проекта, дозапросов нет. Гайдлайны 2026: цифра без направления
      // не даёт решения — рядом с «прогревов/ч» нужен ход за период.
      spark: hourlyCampaignHistory
        .filter(h => h.campaign === selectedProject)
        .slice(-24)
        .map(h => h.count),
      // Состав пачки профилей: срез с сервера раз в час, приходит в
      // «медленном» канале отдельно от счётчиков.
      pool_stats: (selectedProject ? poolStats[selectedProject] : null)
        ?? (cs as any)?.pool_stats,
    }
  }, [selectedProject, campaignStats, currentCampaign, poolStats, hourlyCampaignHistory])

  const hasRunData = useMemo(() => {
    if (!selectedProject) return false
    const camp = currentCampaign as any
    const cs = campaignStats[selectedProject] as any
    const okPer = (cs?.ok ?? camp?.run_ok ?? camp?.last_run_ok ?? 0) as number
    const failPer = (cs?.fail ?? camp?.run_fail ?? camp?.last_run_fail ?? 0) as number
    return okPer > 0 || failPer > 0 || filteredLogs.length > 0
  }, [selectedProject, currentCampaign, campaignStats, filteredLogs])

  useEffect(() => {
    if (campaignRunning && !prevRunningRef.current) {
      setRightTab('results')
    }
    prevRunningRef.current = campaignRunning
  }, [campaignRunning, selectedProject])

  // Profiler equivalent: switch to Results tab when global profiler starts.
  const prevProfilerRunningRef = useRef(false)
  useEffect(() => {
    if (profilerRunning && !prevProfilerRunningRef.current) {
      setRightTab('results')
    }
    prevProfilerRunningRef.current = profilerRunning
  }, [profilerRunning])

  // When switching to a project, default tab based on its running state:
  // stopped → "settings", running → "results" (latter will be ensured by the effect above too).
  const prevSelectedRef = useRef<string | null>(null)
  useEffect(() => {
    if (selectedProject === prevSelectedRef.current) return
    prevSelectedRef.current = selectedProject
    if (!selectedProject) return
    const p = projects.find(x => x.id === selectedProject)
    setRightTab(p?.running ? 'results' : 'settings')
  }, [selectedProject, projects])

  const clearLogs = useCallback(() => { setLogs([]); allLogsRef.current = []; resetStats() }, [resetStats])

  // ── Sidebar project helpers ──

  const getStatusText = (p: any): string => {
    if (p.running) return `Запущен${p.workers_total ? ` (${p.workers_active ?? p.workers_alive ?? 0}/${p.workers_total})` : ''}`
    if (p.paused) return 'На паузе'
    // Bug #134: для continuous/interval/hourly start_time не используется
    // планировщиком — он остаётся со старым значением (часто 05:00 или
    // 09:00 по умолчанию) и вводит оператора в заблуждение «запуск в 5
    // утра», хотя проект на самом деле работает по непрерывному графику.
    // Показываем тип расписания вместо мифической стартовой минуты.
    if (p.sched_enabled) {
      const t = p.sched_type || 'time'
      if (t === 'continuous') return 'Непрерывно'
      if (t === 'interval') return 'С интервалом'
      if (t === 'hourly') return 'Каждый час'
      if ((t === 'time' || t === 'daily') && p.sched_start_time) return `Запуск в ${p.sched_start_time}`
      return 'По расписанию'
    }
    return 'Остановлен'
  }

  // ── Sidebar width ──

  const sidebarPx = sidebarCollapsed ? 56 : 280

  // ── Render nav button ──

  const renderNavButton = (Icon: typeof LayoutDashboard, label: string, isActive: boolean, onClick: () => void, trailing?: React.ReactNode) => {
    const btn = (
      <Button
        variant="ghost"
        onClick={onClick}
        className={`w-full h-9 text-sm ${sidebarCollapsed ? 'justify-center px-0' : 'justify-start gap-2.5'} ${
          isActive ? 'bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15 hover:text-primary' : 'text-muted-foreground'
        }`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        {!sidebarCollapsed && <>{label}{trailing}</>}
      </Button>
    )
    return sidebarCollapsed ? (
      <Tooltip key={label}><TooltipTrigger asChild>{btn}</TooltipTrigger><TooltipContent side="right" className="text-xs">{label}</TooltipContent></Tooltip>
    ) : btn
  }

  // ── Render project item ──

  const renderProjectItem = (p: any, idx: number, isArchived = false) => {
    const isSelected = selectedProject === p.id
    const isRunning = p.running
    const isPaused = !!p.paused
    const barColor = isRunning ? 'var(--success)' : isPaused || p.sched_enabled ? 'var(--warning)' : p.color || (isSelected ? 'var(--primary)' : 'var(--border)')

    const ok = p.run_ok ?? 0
    const fail = p.run_fail ?? 0
    const captcha = p.run_captcha ?? 0
    const uptimeSec = p.started_at ? Math.max(0, Math.floor(Date.now() / 1000 - Number(p.started_at))) : 0
    const fmtUptime = (s: number): string => {
      if (s < 60) return `${s}с`
      const m = Math.floor(s / 60)
      if (m < 60) return `${m}м`
      const h = Math.floor(m / 60); const mr = m % 60
      return mr ? `${h}ч ${mr}м` : `${h}ч`
    }
    const showStats = (isRunning || isPaused) && (ok || fail || captcha || uptimeSec)

    return (
      <div
        key={p.id}
        draggable={!isArchived}
        onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)); (e.currentTarget as HTMLElement).style.opacity = '0.4' }}
        onDragEnd={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1' }}
        onDragOver={(e) => { if (!isArchived) { e.preventDefault(); e.dataTransfer.dropEffect = 'move' } }}
        onDrop={(e) => {
          if (isArchived) return
          e.preventDefault()
          const src = parseInt(e.dataTransfer.getData('text/plain'), 10)
          if (isNaN(src) || src === idx) return
          const reordered = [...activeProjects]; const [moved] = reordered.splice(src, 1); reordered.splice(idx, 0, moved)
          reorderCampaigns(reordered.map(x => x.id)).then(refreshProjects)
        }}
        className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${isArchived ? 'opacity-50' : ''} ${isSelected ? 'bg-accent' : 'hover:bg-accent/50'}`}
        onClick={() => !isArchived && handleSelectProject(p.id)}
      >
        <div className="flex-1 min-w-0">
          {/* Title row */}
          <div className="flex items-center gap-2 min-w-0">
            {isRunning && <span className="relative flex w-1.5 h-1.5 shrink-0"><span className="absolute inset-0 rounded-full bg-success animate-ping opacity-60" /><span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-success" /></span>}
            {isPaused && <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 shrink-0" />}
            {!isRunning && !isPaused && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: barColor, opacity: 0.5 }} />}
            <span className={`text-[13px] font-medium truncate leading-tight flex-1 ${isSelected ? 'text-foreground' : isArchived ? 'text-muted-foreground' : 'text-foreground/90'}`}>{p.name}</span>
            {showStats && (ok + fail > 0) && (
              <span className={`text-[11px] tabular-nums font-semibold shrink-0 ${
                Math.round((ok / (ok + fail)) * 100) >= 90 ? 'text-success'
                  : Math.round((ok / (ok + fail)) * 100) >= 70 ? 'text-yellow-500'
                  : 'text-destructive'
              }`}>
                {Math.round((ok / (ok + fail)) * 100)}%
              </span>
            )}
          </div>

          {/* Progress bar — success rate */}
          {showStats && (ok + fail > 0) && (() => {
            const rate = Math.round((ok / (ok + fail)) * 100)
            const barCls = rate >= 90 ? 'bg-success' : rate >= 70 ? 'bg-yellow-500' : 'bg-destructive'
            return (
              <div className="h-[3px] mt-1.5 bg-muted/50 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${barCls}`} style={{ width: `${rate}%` }} />
              </div>
            )
          })()}

          {/* Meta row */}
          {isArchived ? (
            <div className="text-[11px] text-muted-foreground/70 mt-1 leading-tight">В архиве</div>
          ) : showStats ? (
            <div className="flex items-center gap-1.5 mt-1.5 text-[11px] tabular-nums leading-none text-muted-foreground/80">
              {uptimeSec > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5 opacity-70" />
                  {fmtUptime(uptimeSec)}
                </span>
              )}
              {(ok + fail) > 0 && (<>
                <span className="text-muted-foreground/30">·</span>
                <span>{ok + fail} выполн.</span>
              </>)}
              {captcha > 0 && (<>
                <span className="text-muted-foreground/30">·</span>
                <span>{captcha} капч</span>
              </>)}
            </div>
          ) : p.sched_enabled ? (
            <div className="text-[11px] text-yellow-500/90 mt-0.5 leading-tight truncate">По расписанию</div>
          ) : null}
        </div>
        <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          {!isArchived && (sidebarActionId === p.id ? (
            <Button variant="ghost" size="icon" className="h-6 w-6" disabled><Loader2 className="w-3 h-3 animate-spin" /></Button>
          ) : isRunning ? (<>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-yellow-500 hover:bg-yellow-500/10" title="Пауза" onClick={async (e) => {
              e.stopPropagation(); setSidebarActionId(p.id); await pauseCampaign(p.id); setAlert({ type: 'success', text: `${p.name} — на паузе` }); setTimeout(() => { refreshProjects(); setSidebarActionId(null) }, 1000)
            }}><Pause className="w-3 h-3" /></Button>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:bg-destructive/10" title="Стоп" onClick={async (e) => {
              e.stopPropagation()
              if (!await appConfirm({ title: `Остановить «${p.name}»?`, description: 'Все активные воркеры будут немедленно завершены.', confirmText: 'Остановить', destructive: true })) return
              setSidebarActionId(p.id); await stopCampaign(p.id); setAlert({ type: 'success', text: `${p.name} — остановлен` }); setTimeout(() => { refreshProjects(); setSidebarActionId(null) }, 1000)
            }}><Square className="w-3 h-3" /></Button>
          </>) : isPaused ? (<>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-primary hover:bg-primary/10" title="Возобновить" onClick={async (e) => {
              e.stopPropagation(); setSidebarActionId(p.id); await resumeCampaign(p.id); setAlert({ type: 'success', text: `${p.name} — возобновлён` }); setTimeout(() => { refreshProjects(); setSidebarActionId(null) }, 1000)
            }}><Play className="w-3 h-3" /></Button>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:bg-destructive/10" title="Стоп" onClick={async (e) => {
              e.stopPropagation()
              if (!await appConfirm({ title: `Остановить «${p.name}»?`, description: 'Все активные воркеры будут немедленно завершены.', confirmText: 'Остановить', destructive: true })) return
              setSidebarActionId(p.id); await stopCampaign(p.id); setAlert({ type: 'success', text: `${p.name} — остановлен` }); setTimeout(() => { refreshProjects(); setSidebarActionId(null) }, 1000)
            }}><Square className="w-3 h-3" /></Button>
          </>) : (
            <Button variant="ghost" size="icon" className="h-6 w-6 text-primary hover:bg-primary/10" title="Запустить" onClick={async (e) => {
              e.stopPropagation(); setSidebarActionId(p.id); await startCampaign(p.id); setAlert({ type: 'success', text: `${p.name} — запущен` }); setTimeout(() => { refreshProjects(); setSidebarActionId(null) }, 1000)
            }}><Play className="w-3 h-3" /></Button>
          ))}
        </div>
      </div>
    )
  }

  // ── Project toolbar ──

  const renderProfilerToolbar = () => {
    // Имя текущего профайлерского проекта (если selectedProject выбран).
    const projName = selectedProject
      ? (projects.find(p => p.id === selectedProject)?.name || 'Profiler')
      : 'Profiler'
    return (
      <div className="shrink-0 px-2 py-2.5 md:px-6 md:py-4 border-b border-border/50 space-y-2 md:space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2 md:gap-2.5">
                <h1 className="text-base md:text-xl font-semibold tracking-tight text-foreground truncate">{projName}</h1>
                {profilerRunning && <Badge variant="outline" className="gap-1 text-[10px] md:text-xs px-1.5 md:px-2.5 border-success/30 text-success bg-success/10 shrink-0" title="Работает"><span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" /><span className="hidden md:inline">Работает</span></Badge>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 md:gap-2 shrink-0 flex-wrap">
            {isDirty && canEdit && (
              <Button variant="outline" size="sm" disabled={isSaving} className="h-8 px-2 gap-1 md:h-9 md:px-4 md:gap-2" onClick={handleSaveConfig} title="Сохранить">
                <Save className="w-4 h-4 shrink-0" /><span className="hidden md:inline">{isSaving ? 'Сохраняем…' : 'Сохранить'}</span>
              </Button>
            )}
            {profilerRunning ? (
              <Button variant="destructive" size="sm" className="h-8 px-2 gap-1 md:h-9 md:px-4 md:gap-2" onClick={handleProfilerStop} disabled={actionInProgress === 'stop'} title="Остановить">
                {actionInProgress === 'stop' ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Square className="w-4 h-4 shrink-0" />}
                <span className="hidden md:inline">Остановить</span>
              </Button>
            ) : (
              <Button size="sm" className="h-8 px-2 gap-1 md:h-9 md:px-4 md:gap-2" onClick={handleProfilerStart} disabled={actionInProgress === 'start'} title="Запуск">
                {actionInProgress === 'start' ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Play className="w-4 h-4 shrink-0" />}
                <span className="hidden md:inline">Запуск</span>
              </Button>
            )}
          </div>
        </div>
        {/* Табы «Настройки / Статистика» — показываем только когда выбран проект,
            чтобы видеть live-потоки/результаты во время прогрева. */}
        {selectedProject && (
          <div className="flex items-center gap-1 border border-border/50 rounded-lg p-0.5 w-fit">
            <button
              onClick={() => setRightTab('settings')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${rightTab === 'settings' ? 'bg-accent text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Настройки
            </button>
            <button
              onClick={() => setRightTab('results')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${rightTab === 'results' ? 'bg-accent text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Статистика
              {profilerRunning && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-success animate-pulse" />}
            </button>
          </div>
        )}
      </div>
    )
  }

  const renderToolbar = () => {
    // Для профайлера используем тот же стиль toolbar что и для бустера,
    // но пропускаем бустер-специфичное (sites_file проверка, etc.)
    if (activeProduct === 'profiler') {
      if (!selectedProject) return renderProfilerToolbar()
      // Для конкретного проекта — используем бустерский шаблон toolbar
      // (имя, цвет, tabs Настройки/Статистика, кнопки старт/стоп)
    }
    if (!currentCampaign || !selectedProject) return null
    const c = currentCampaign as any
    const schedEnabled = activeCfg?.SCHED_ENABLED === 'Да'
    const schedType = activeCfg?.SCHED_TYPE || 'time'
    const schedStartTime = activeCfg?.SCHED_START_TIME || '09:00'
    const scheduleLabel = schedEnabled
      ? schedType === 'interval' ? `Интервал ${activeCfg?.SCHED_INTERVAL_MINUTES || 10} мин`
        : schedType === 'hourly' ? 'Каждый час'
        : schedType === 'continuous' ? 'Непрерывно'
        : `В ${schedStartTime}`
      : null

    const scheduleObj = {
      enabled: schedEnabled, type: schedType, start_time: schedStartTime,
      days: activeCfg?.SCHED_DAYS || 'Каждый день', days_custom: activeCfg?.SCHED_DAYS_CUSTOM || '',
      work_from: activeCfg?.SCHED_WORK_FROM || '00:00', work_to: activeCfg?.SCHED_WORK_TO || '23:59',
      run_hours: Number(activeCfg?.SCHED_RUN_HOURS) || 0, run_minutes: Number(activeCfg?.SCHED_RUN_MINUTES) || 0,
      interval_minutes: Number(activeCfg?.SCHED_INTERVAL_MINUTES) || 10,
      max_runs: Number(activeCfg?.SCHED_RESTART_MAX) || 0,
      restart: activeCfg?.SCHED_RESTART === 'Да', restart_delay: Number(activeCfg?.SCHED_RESTART_DELAY) || 5,
      hourly_minute: Number(activeCfg?.SCHED_HOURLY_MINUTE) || 0,
    }
    const handleSchedToggle = async (val: boolean) => {
      const newCfg = { ...selectedCfg, SCHED_ENABLED: val ? 'Да' : 'Нет' }
      setSelectedCfg(newCfg)
      await updateCampaign(selectedProject!, { schedule: { ...scheduleObj, enabled: val } })
      refreshProjects()
    }

    return (
      <TooltipProvider delayDuration={200}>
      <div className="shrink-0 px-2 md:px-6 py-2.5 md:py-4 border-b border-border/50 space-y-2 md:space-y-3 min-w-0">
        {/* Row 1: Name+status */}
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          {c.color && <div className="w-1 h-7 md:h-8 rounded-full shrink-0" style={{ background: c.color }} />}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 md:gap-2.5">
              <h1 className="text-lg md:text-xl font-semibold tracking-tight text-foreground truncate">{c.name}</h1>
              {canEdit && (
                <button type="button" onClick={() => handleEditProject(c.id)} className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" title="Редактировать проект"><Pencil className="w-4 h-4" /></button>
              )}
              {c.running && <Badge variant="outline" className="gap-0.5 md:gap-1 text-[10px] md:text-xs px-1.5 md:px-2.5 border-success/30 text-success bg-success/10 shrink-0" title="Работает"><span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" /><span className="hidden md:inline">Работает</span></Badge>}
              {c.paused && <Badge variant="outline" className="gap-0.5 md:gap-1 text-[10px] md:text-xs px-1.5 md:px-2.5 border-yellow-500/30 text-yellow-500 bg-yellow-500/10 shrink-0" title="На паузе"><Pause className="w-3 h-3 shrink-0" /><span className="hidden md:inline">На паузе</span></Badge>}
            </div>
            {c.comment && <p className="text-xs md:text-sm text-muted-foreground truncate mt-0.5">{c.comment}</p>}
          </div>
        </div>
        <div className="h-[5px]" />

        {/* Row 2: Actions (Mobile: first line = buttons + dropdown; second line = tabs) */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 min-w-0 max-w-[69rem]">
          {/* Line 1 (mobile+desktop): action buttons + dropdown */}
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-wrap">
          {campaignRunning ? (<>
            <Button variant="destructive" size="sm" className="h-9 px-3 gap-1.5 md:h-[42px] md:px-5 md:gap-2 text-sm" onClick={activeProduct === 'profiler' ? handleProfilerStop : handleStop} disabled={actionInProgress === 'stop'} title="Остановить проект">
              {actionInProgress === 'stop' ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Square className="w-4 h-4 shrink-0" />}
              <span>Остановить</span>
            </Button>
            <Button variant="outline" size="sm" className="h-9 px-3 gap-1.5 md:h-[42px] md:px-5 md:gap-2 text-sm bg-transparent text-muted-foreground hover:text-foreground" onClick={handleRestart} disabled={!!actionInProgress} title="Перезапуск">
              {actionInProgress === 'stop' ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <RotateCcw className="w-4 h-4 shrink-0" />}
              <span>Перезапуск</span>
            </Button>
          </>) : (
            <Button size="sm" className="h-9 px-3 gap-1.5 md:h-[42px] md:px-5 md:gap-2 text-sm" onClick={activeProduct === 'profiler' ? handleProfilerStart : handleStart} disabled={!selectedProject || (activeProduct === 'booster' && selectedCfg.API_WORKER !== 'Да' && !campaignFiles.sites_file) || actionInProgress === 'start'} title="Запуск">
              {actionInProgress === 'start' ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Play className="w-4 h-4 shrink-0" />}
              <span>Запуск</span>
            </Button>
          )}
          {isDirty && canEdit && (
            <Button variant="outline" size="sm" disabled={isSaving} className="h-9 px-3 gap-1.5 md:h-[42px] md:px-5 md:gap-2 text-sm bg-transparent" onClick={handleSaveConfig} title="Сохранить">
              <Save className="w-4 h-4 shrink-0" /><span>{isSaving ? 'Сохраняем…' : 'Сохранить'}</span>
            </Button>
          )}

          {/* Mobile dropdown — pushed to the right of action buttons */}
          <div className="flex md:hidden items-center shrink-0 ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => setShowSchedule(true)} className="gap-2">
                  <Calendar className={`w-4 h-4 ${schedEnabled ? 'text-primary' : ''}`} />
                  {schedEnabled ? `Расписание: ${scheduleLabel}` : 'Расписание'}
                </DropdownMenuItem>
                {canFull && (<>
                  <DropdownMenuItem onClick={() => exportCampaignConfig(selectedProject!)} className="gap-2">
                    <Upload className="w-4 h-4" /> Экспорт настроек
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => uploadRef.current?.click()} className="gap-2">
                    <Download className="w-4 h-4" /> Импорт настроек
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleDeleteProject(selectedProject!)} className="gap-2 text-destructive focus:text-destructive">
                    <Trash2 className="w-4 h-4" /> Удалить проект
                  </DropdownMenuItem>
                </>)}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          </div>

          {/* Line 2 (mobile): tabs Настройки/Статистика on full width */}
          {(campaignRunning || hasRunData || (activeProduct === 'profiler' && profilerRunning)) && (
            <div className="flex items-center gap-1 rounded-lg bg-muted/60 p-1 md:p-1.5 w-full md:w-auto">
              <Button variant="ghost" size="sm" onClick={() => setRightTab('settings')} title="Настройки" className={`h-9 text-xs flex-1 md:flex-none md:w-[120px] justify-center gap-1.5 rounded-md ${rightTab === 'settings' ? 'bg-background text-foreground shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                <Settings2 className="w-3.5 h-3.5 shrink-0" />
                <span>Настройки</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setRightTab('results')} title="Статистика" className={`h-9 text-xs flex-1 md:flex-none md:w-[120px] justify-center gap-1.5 rounded-md ${rightTab === 'results' ? 'bg-background text-foreground shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                <BarChart3 className="w-3.5 h-3.5 shrink-0" />
                <span>Статистика</span>
              </Button>
            </div>
          )}

          {/* Desktop: inline icon buttons */}
          <div className="hidden md:flex items-center gap-1.5 shrink-0">
            <Tooltip><TooltipTrigger asChild>
              <button type="button" onClick={() => setShowSchedule(true)} className={`inline-flex items-center justify-center w-9 h-9 rounded-md border transition-colors ${schedEnabled ? 'text-primary border-primary/30 bg-primary/10 hover:bg-primary/15' : 'text-muted-foreground border-border hover:text-foreground hover:bg-accent'}`}>
                <Calendar className="w-4 h-4" />
              </button>
            </TooltipTrigger><TooltipContent side="bottom" className="text-xs">{schedEnabled ? `Расписание: ${scheduleLabel}` : 'Расписание'}</TooltipContent></Tooltip>
            {canFull && (<>
              <Tooltip><TooltipTrigger asChild>
                <button type="button" onClick={() => exportCampaignConfig(selectedProject!)} className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
                  <Upload className="w-4 h-4" />
                </button>
              </TooltipTrigger><TooltipContent side="bottom" className="text-xs">Экспорт настроек</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild>
                <button type="button" onClick={() => uploadRef.current?.click()} className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
                  <Download className="w-4 h-4" />
                </button>
              </TooltipTrigger><TooltipContent side="bottom" className="text-xs">Импорт настроек</TooltipContent></Tooltip>
              <div className="w-px h-5 bg-border mx-0.5" />
              <Tooltip><TooltipTrigger asChild>
                <button type="button" onClick={() => handleDeleteProject(selectedProject!)} className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </TooltipTrigger><TooltipContent side="bottom" className="text-xs">Удалить проект</TooltipContent></Tooltip>
            </>)}
          </div>
          <input ref={uploadRef} type="file" accept=".json" className="hidden" onChange={async (e) => {
            const file = e.target.files?.[0]; if (!file || !selectedProject) return
            try { const text = await file.text(); const data = JSON.parse(text); await importCampaignConfig(selectedProject, data); setAlert({ type: 'success', text: 'Настройки импортированы' }); loadProject(selectedProject) }
            catch { setAlert({ type: 'error', text: 'Ошибка при импорте файла' }) }
            e.target.value = ''
          }} />
        </div>
      </div>
      </TooltipProvider>
    )
  }

  // ── Upload block for Настройки сайта ──

  const renderUploadBlock = selectedProject && selectedSection === 'Настройки сайта' ? (
    <TooltipProvider delayDuration={300}>
      <div className="mb-4 md:mb-5 space-y-2 md:space-y-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3 min-w-0">
          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">Формат:</span>
          <div className="flex items-center gap-1 p-0.5 md:gap-1.5 rounded-lg bg-muted/50 min-w-0 overflow-x-auto">
            {['LR в файле с Сайтами (url.txt)', 'LR в файле с Ключами (site.ru.txt)'].map(fmt => {
              const isActive = (activeCfg?.FORMAT_WORKER || 'LR в файле с Сайтами (url.txt)') === fmt
              const label = fmt.includes('Сайтами') ? 'sites.txt + ключи' : 'site.ru.txt (ключи+LR)'
              return (
                <button key={fmt} onClick={() => handleCfgUpdate({ FORMAT_WORKER: fmt })}
                  className={`text-[11px] md:text-xs px-2 py-1 md:px-3 md:py-1.5 rounded-md transition-all cursor-pointer whitespace-nowrap shrink-0 ${isActive ? 'bg-background text-foreground shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                  {label}
                </button>
              )
            })}
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/50 cursor-help shrink-0" />
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[280px] text-xs leading-relaxed">
              <p className="font-medium mb-1">sites.txt + ключи:</p>
              <p className="mb-1.5"><code className="bg-muted px-1 rounded">sites.txt</code> — список сайтов, <code className="bg-muted px-1 rounded">domain.txt</code> — ключи</p>
              <p className="font-medium mb-1">site.ru.txt (ключи+LR):</p>
              <p>Без sites.txt — каждый файл <code className="bg-muted px-1 rounded">domain.txt</code> содержит ключ;кол-во;регион</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-2.5 min-w-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <label className="text-xs md:text-sm font-medium inline-flex items-center gap-1.5 md:gap-2 cursor-pointer px-3 py-2 md:px-4 md:py-2.5 rounded-lg transition-colors hover:bg-accent text-foreground border border-dashed border-border whitespace-nowrap shrink-0 max-w-full">
                <FolderUp className="w-4 h-4 shrink-0" />Загрузить папку
                <input type="file" className="hidden" {...{ webkitdirectory: '', directory: '' } as any} multiple onChange={async (e) => {
                  const files = e.target.files; if (!files?.length || !selectedProject) return
                  const txtFiles = Array.from(files).filter(f => f.name.endsWith('.txt'))
                  if (txtFiles.length) {
                    await uploadCampaignFolder(selectedProject, txtFiles)
                    delete projectCacheRef.current[selectedProject]
                    getCampaignFiles(selectedProject).then(setCampaignFiles).catch(() => {})
                    e.target.value = ''
                  }
                }} />
              </label>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-relaxed">
              <p className="font-medium mb-1">Структура папки:</p>
              <p><code className="bg-muted px-1 rounded">sites.txt</code> — список сайтов (site|region|brand)</p>
              <p><code className="bg-muted px-1 rounded">domain.txt</code> — ключевые слова для каждого сайта</p>
            </TooltipContent>
          </Tooltip>

          <span className="text-muted-foreground/30 text-xs text-center md:text-left">или</span>

          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={() => setTaskGeneratorTarget(selectedProject)} className="text-xs md:text-sm font-medium inline-flex items-center gap-1.5 md:gap-2 px-3 py-2 md:px-4 md:py-2.5 rounded-lg transition-colors hover:bg-primary/10 text-primary border border-primary/20 cursor-pointer whitespace-nowrap shrink-0 max-w-full"><Wand2 className="w-4 h-4 shrink-0" />Создать задание</button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[220px] text-xs">Создать файлы заданий прямо в интерфейсе — укажите сайты, регионы и ключевые слова</TooltipContent>
          </Tooltip>
        </div>

        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/20">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-600 dark:text-amber-500 mt-0.5" />
          <p className="text-xs text-amber-800 dark:text-amber-200/80 leading-relaxed">
            <span className="font-medium text-amber-400">Загружайте именно папку</span>, а не отдельные файлы. Внутри папки должны быть <code className="bg-muted/50 px-1 rounded text-[10px]">sites.txt</code> и файлы ключей <code className="bg-muted/50 px-1 rounded text-[10px]">domain.txt</code>. Отдельные .txt файлы без структуры папки не будут обработаны.
          </p>
        </div>

        {campaignFiles.sites_file && (
          <div className="flex flex-wrap items-center gap-2 md:gap-4 px-2 py-2 md:px-4 md:py-2.5 rounded-lg bg-card border border-border cursor-pointer transition-colors hover:bg-accent/50 min-w-0" onClick={() => setFileEditorTarget(selectedProject)}>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <FileText className="w-3.5 h-3.5 shrink-0" />
              <span>Сайтов:</span>
              <strong className="text-foreground">{campaignFiles.sites_file.count}</strong>
            </div>
            {campaignFiles.key_files.length > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Key className="w-3.5 h-3.5 shrink-0" />
                <span>Ключей:</span>
                <strong className="text-foreground">{campaignFiles.key_files.reduce((s: number, f: any) => s + f.keys_count, 0)}</strong>
                <span className="text-muted-foreground/50">({campaignFiles.key_files.length} файлов)</span>
              </div>
            )}
            <span className="ml-auto text-primary text-xs font-medium whitespace-nowrap">Просмотр →</span>
          </div>
        )}
      </div>
    </TooltipProvider>
  ) : undefined

  // ═══════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════

  const runningProjects = activeProjects.filter(p => (p as any).running || (p as any).paused)
  const stoppedProjects = activeProjects.filter(p => !(p as any).running && !(p as any).paused)
  const [projectFilter, setProjectFilter] = useUserPref<'active' | 'stopped' | 'all'>('cb-project-filter', 'all')
  // Видимость пунктов сайдбара (per-user, серверный pref). Массив id СКРЫТЫХ
  // пунктов. Критичные (settings) никогда не скрываются — см. navItems.ts.
  const [sidebarHiddenRaw] = useUserPref<string[]>(SIDEBAR_HIDDEN_PREF_KEY, [])
  const _sidebarHidden = useMemo(() => normalizeHiddenSet(sidebarHiddenRaw), [sidebarHiddenRaw])
  const _navHidden = useCallback((id: string) => _sidebarHidden.has(id), [_sidebarHidden])
  // Если под выбранным фильтром 0 элементов — auto-fallback на 'all',
  // чтобы sidebar никогда не выглядел пустым.
  const _rawFiltered = projectFilter === 'active' ? runningProjects
    : projectFilter === 'stopped' ? stoppedProjects
    : activeProjects
  const filteredProjects = (_rawFiltered.length === 0 && activeProjects.length > 0)
    ? activeProjects
    : _rawFiltered
  const _effectiveFilter: 'active' | 'stopped' | 'all' =
    (_rawFiltered.length === 0 && activeProjects.length > 0) ? 'all' : projectFilter

  return (
    <div className="h-[100dvh] flex flex-col p-1 md:p-2.5 gap-1 md:gap-2.5 bg-background min-w-0 overflow-x-hidden">
      {/* ── HEADER ── */}
      <header className="flex items-center justify-between gap-2 px-2 md:px-5 py-2 md:py-3 shrink-0 rounded-lg bg-card border border-border min-w-0">
        <div className="flex items-center gap-1.5 md:gap-3 min-w-0 flex-1">
          {/* Mobile hamburger */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden h-8 w-8 shrink-0">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[min(100vw-1rem,320px)] p-0 overflow-y-auto flex flex-col">
              <div className="p-4 space-y-1 flex-1 min-h-0 overflow-y-auto">
                {/* Переключатель серверов (рендерится только при 2+ серверах
                    лицензии). На десктопе он в сайдбаре — на мобиле его не
                    было видно, т.к. сайдбар скрыт `hidden md:flex`. НЕ
                    закрываем Sheet по клику здесь: иначе клик по триггеру
                    Popover закрыл бы меню до открытия дропдауна. Переход на
                    другой сервер = полная перезагрузка страницы. */}
                <div className="-mx-2 mb-2">
                  <ServerSwitcher collapsed={false} initialServers={myServersList} />
                </div>
                {(() => {
                  const baseItems = [
                    _hasPerm('dashboard') && !_navHidden('dashboard') && { icon: LayoutDashboard, label: 'Рабочий стол', active: activeProduct === 'profiler' ? showProfilerOverview || isOverview : isOverview, action: () => { navigate('overview'); setMobileMenuOpen(false) } },
                    _hasPerm('projects') && !_navHidden('projects') && activeProduct !== 'profiler' && { icon: FolderOpen, label: 'Мои проекты', active: showProjects, action: () => { navigate('projects'); setMobileMenuOpen(false) } },
                    _hasPerm('projects') && !_navHidden('projects') && activeProduct === 'profiler' && { icon: FolderOpen, label: 'Мои проекты', active: showProfilerProjects, action: () => { navigate('profiler-projects'); setMobileMenuOpen(false) } },
                    _hasPerm('identifiers') && !_navHidden('identifiers') && activeProduct !== 'profiler' && { icon: Fingerprint, label: 'Идентификаторы', active: showIdentifiers, action: () => { navigate('identifiers'); setMobileMenuOpen(false) } },
                    _hasPerm('settings') && { icon: Settings2, label: 'Настройки панели', active: showSettings, action: () => { navigate('settings'); setMobileMenuOpen(false) } },
                    _hasPerm('tools') && !_navHidden('tools') && { icon: Wrench, label: 'Инструменты', active: showTools, action: () => { navigate('tools'); setMobileMenuOpen(false) } },
                    _hasPerm('events') && !_navHidden('events') && !IS_PROFILER_ONLY && { icon: Newspaper, label: 'Важные события', active: showEvents, action: () => { navigate('events'); setMobileMenuOpen(false) } },
                    _hasPerm('ideas') && !_navHidden('ideas') && activeProduct !== 'profiler' && { icon: Lightbulb, label: 'Идеи и предложения', active: showIdeas, action: () => { navigate('ideas'); setMobileMenuOpen(false) } },
                    _hasPerm('bugs') && !_navHidden('bugs') && activeProduct !== 'profiler' && { icon: Bug, label: 'Баги и ошибки', active: showBugs, action: () => { navigate('bugs'); setMobileMenuOpen(false) } },
                    /* Предзаказ серверов перенесён в Настройки → «Лицензия и оплата» */
                  ].filter(Boolean) as Array<{ icon: any; label: string; active: boolean; action: () => void }>
                  const adminItems = [
                    _hasCentralPerm('licenses') && !_navHidden('licenses') && { icon: Key, label: 'Лицензии', active: showLicenses, action: () => { navigate('licenses'); setMobileMenuOpen(false) } },
                    _hasCentralPerm('work_servers') && !_navHidden('work_servers') && { icon: Server, label: 'Рабочие серверы', active: showWorkServers, action: () => { navigate('work-servers'); setMobileMenuOpen(false) } },
                    _hasCentralPerm('server_orders_admin') && !_navHidden('server_orders_admin') && { icon: Server, label: 'Аренда серверов', active: showServerOrdersAdmin, action: () => { navigate('server-orders-admin'); setMobileMenuOpen(false) } },
                    _hasCentralPerm('deployments') && !_navHidden('deployments') && { icon: Rocket, label: 'Деплой', active: showDeployments, action: () => { navigate('deployments'); setMobileMenuOpen(false) } },
                    _hasPerm('internal_docs') && !_navHidden('internal_docs') && hasInternalDocs && { icon: BookOpen, label: 'Документация', active: showDocs, action: () => { navigate('docs'); setMobileMenuOpen(false) } },
                    _hasCentralPerm('fingerprints') && !_navHidden('fingerprints') && { icon: Fingerprint, label: 'Отпечатки', active: showFingerprints, action: () => { navigate('fingerprints'); setMobileMenuOpen(false) } },
                    _hasCentralPerm('identifiers_admin') && !_navHidden('identifiers_admin') && { icon: IdCard, label: 'Управление идентификаторами', active: showIdentifiersAdmin, action: () => { navigate('identifiers-admin'); setMobileMenuOpen(false) } },
                    _hasModerationPerm('ideas_admin') && !_navHidden('ideas_admin') && { icon: Lightbulb, label: 'Идеи (модерация)', active: showIdeasAdmin, action: () => { navigate('ideas-admin'); setMobileMenuOpen(false) } },
                    _hasModerationPerm('bugs_admin') && !_navHidden('bugs_admin') && { icon: Bug, label: 'Баги (модерация)', active: showBugsAdmin, action: () => { navigate('bugs-admin'); setMobileMenuOpen(false) } },
                    _hasCentralPerm('ai_tickets') && !_navHidden('ai_tickets') && { icon: Sparkles, label: 'AI-тикеты', active: showAiTickets, action: () => { navigate('ai-tickets-admin'); setMobileMenuOpen(false) } },
                    _hasModerationPerm('alarms') && !_navHidden('alarms') && { icon: Megaphone, label: 'Новости и уведомления', active: showAlarmsAdmin, action: () => { navigate('alarms-admin'); setMobileMenuOpen(false) } },
                    _canEditScenario && !_navHidden('scenario_editor') && { icon: Workflow, label: 'Редактор сценария', active: showScenarioEditor, action: () => { navigate('scenario-editor'); setMobileMenuOpen(false) } },
                  ].filter(Boolean) as Array<{ icon: any; label: string; active: boolean; action: () => void }>
                  return (
                    <>
                      {baseItems.length > 0 && (<>
                        <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground/60 px-3 pb-2">Навигация</div>
                        {baseItems.map(item => (
                          <button key={item.label} onClick={item.action} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${item.active ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                            <item.icon className="w-4 h-4 shrink-0" /> {item.label}
                          </button>
                        ))}
                      </>)}
                      {adminItems.length > 0 && (<>
                        <Separator className="my-3" />
                        <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground/60 px-3 pb-2">Администрирование</div>
                        {adminItems.map(item => (
                          <button key={item.label} onClick={item.action} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${item.active ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                            <item.icon className="w-4 h-4 shrink-0" /> {item.label}
                          </button>
                        ))}
                      </>)}
                    </>
                  )
                })()}
                {_hasPerm('projects') && activeProduct !== 'profiler' && activeProjects.length > 0 && (<>
                  <Separator className="my-3" />
                  <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground/60 px-3 pb-2">Проекты ({activeProjects.length})</div>
                  <div className="flex flex-col gap-1" onClick={() => setMobileMenuOpen(false)}>
                    {activeProjects.map((c, i) => renderProjectItem(c, i))}
                  </div>
                </>)}
              </div>
            </SheetContent>
          </Sheet>
          <button onClick={() => navigate('overview')}
                  aria-label="Главная"
                  className="flex items-center justify-center cursor-pointer hover:brightness-110 active:scale-95 transition shrink-0">
            <CreepMark size={34} />
          </button>
          <div className="leading-tight min-w-0 hidden md:block">
            <CreepLogo size={15} />
            <div className="text-[10px] font-bold text-muted-foreground/60 truncate mt-0.5">
              {serverName || (activeProduct === 'profiler' ? 'PROFILER PANEL' : 'BOOSTER PANEL')}
            </div>
          </div>
          {hasBothProducts && (
            <div className="flex items-center ml-2 pl-2 gap-1 md:ml-4 md:pl-4 rounded-xl p-1 md:p-1.5 border-l border-border bg-muted/60 shrink-0">
              <Button variant="ghost" size="sm"
                onClick={() => { setActiveProduct('profiler'); setSelectedProject(null); setTimeout(() => navigate('profiler-overview'), 0) }}
                className={`h-7 w-7 shrink-0 p-0 md:w-auto md:px-3 text-xs ${activeProduct === 'profiler' ? 'bg-background text-foreground shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                title="Profiler">
                <Brain className="w-4 h-4 md:hidden shrink-0" />
                <span className="hidden md:inline">Profiler</span>
              </Button>
              <Button variant="ghost" size="sm"
                onClick={() => { setActiveProduct('booster'); setSelectedProject(null); setTimeout(() => navigate('overview'), 0); refreshProjects() }}
                className={`h-7 w-7 shrink-0 p-0 md:w-auto md:px-3 text-xs ${activeProduct === 'booster' ? 'bg-background text-foreground shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                title="Booster">
                <Sparkles className="w-4 h-4 md:hidden shrink-0" />
                <span className="hidden md:inline">Booster</span>
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 ml-auto shrink-0">
          <LicenseBadge lic={licInfo} onRenew={() => navigate('settings')} />
          {vpnStatus && (
            <TooltipProvider delayDuration={200}>
              <Tooltip><TooltipTrigger asChild>
                <button type="button" onClick={() => navigate({ tool: 'vpn-setup' })} className={`hidden md:flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium border cursor-pointer transition-colors hover:brightness-125 ${vpnStatus.connected ? 'border-success/30 text-success bg-success/10 hover:bg-success/20' : 'border-destructive/30 text-destructive bg-destructive/10 hover:bg-destructive/20'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${vpnStatus.connected ? 'bg-success' : 'bg-destructive animate-pulse'}`} />
                  VPN
                </button>
              </TooltipTrigger><TooltipContent side="bottom" className="text-xs">{vpnStatus.connected ? `VPN подключён${vpnStatus.name ? ` · ${vpnStatus.name}` : ''}${vpnStatus.vpn_ip ? ` (${vpnStatus.vpn_ip})` : ''} · открыть настройки` : 'VPN отключён — открыть настройки VPN'}</TooltipContent></Tooltip>
            </TooltipProvider>
          )}
          <button onClick={toggleTheme} title={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            className="relative inline-flex h-7 w-12 md:w-14 items-center rounded-md border border-border bg-muted transition-colors shrink-0">
            <span className={`absolute left-0.5 flex h-6 w-6 items-center justify-center rounded-[calc(var(--radius)-2px)] bg-primary text-primary-foreground shadow-sm transition-transform duration-200 ${theme === 'dark' ? 'translate-x-5 md:translate-x-[26px]' : 'translate-x-0'}`}>
              {theme === 'dark' ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
            </span>
          </button>
          <Separator orientation="vertical" className="mx-1 h-5" />
          <Button variant="ghost" size="icon" onClick={onLogout} title="Выход" className="h-8 w-8"><LogOut className="w-4 h-4" /></Button>
        </div>
      </header>

      {/* ── MAIN LAYOUT ── */}
      <div className="flex flex-1 min-h-0 min-w-0 gap-0 md:gap-2.5">
        {/* ── SIDEBAR (desktop) ── */}
        <nav className="hidden md:flex flex-col shrink-0 overflow-hidden rounded-lg bg-sidebar border border-sidebar-border transition-[width] duration-200 ease-in-out" style={{ width: sidebarPx }}>
          <div className={`flex items-center shrink-0 ${sidebarCollapsed ? 'justify-center px-1.5 py-2' : 'justify-end px-2 pt-2 pb-1'}`}>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => { const next = !sidebarCollapsed; setSidebarCollapsed(next); localStorage.setItem('sidebar_collapsed', next ? '1' : '0') }} className="h-7 w-7 text-muted-foreground hover:text-foreground">
                    {sidebarCollapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="text-xs">{sidebarCollapsed ? 'Развернуть панель' : 'Свернуть панель'}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Server switcher (only renders when license has 2+ servers) */}
          <ServerSwitcher collapsed={sidebarCollapsed} initialServers={myServersList} />

          {/* Баланс/срок: яркое предупреждение до приостановки серверов.
              Компонент сам тянет /api/balance и скрывается, если тревоги нет. */}
          {!sidebarCollapsed && <BalanceBanner onGoToBalance={() => navigate('settings')} />}

          {/* Update banner */}
          {!sidebarCollapsed && availableUpdate && availableUpdate.version !== appVersion && (
            <div className="mx-3 mb-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
              <div className="flex items-center gap-2 mb-1.5"><Sparkles className="w-4 h-4 text-primary shrink-0" /><span className="text-sm font-medium text-primary">Доступна v{availableUpdate.version}</span></div>
              <p className="text-[11px] text-muted-foreground mb-2 leading-relaxed">Нажмите для обновления. Панель будет перезапущена.</p>
              <Button size="sm" className="w-full h-7 text-xs gap-1.5" disabled={isUpdating} onClick={handleInstallUpdate}>
                {isUpdating ? <><Loader2 className="w-3 h-3 animate-spin" />Обновление...</> : <><Sparkles className="w-3 h-3" />Обновить</>}
              </Button>
            </div>
          )}

          <TooltipProvider delayDuration={200}>
            {/* Навигация — каждый пункт показывается по своему permission.
                Если у юзера нет ни одного permission из секции — секция
                целиком скрывается. Super-admin всегда видит всё. */}
            {(() => {
              const baseItems = [
                _hasPerm('dashboard') && !_navHidden('dashboard') && renderNavButton(LayoutDashboard, 'Рабочий стол', activeProduct === 'profiler' ? showProfilerOverview || isOverview : isOverview, () => navigate('overview'), anyRunning || profilerRunning ? <span className="ml-auto w-1.5 h-1.5 rounded-full bg-success animate-pulse" /> : null),
                _hasPerm('projects') && !_navHidden('projects') && activeProduct !== 'profiler' && renderNavButton(FolderOpen, 'Мои проекты', showProjects, () => navigate('projects'), <Badge variant="secondary" className="ml-auto h-4 px-1.5 text-[9px] tabular-nums">{activeProjects.length}</Badge>),
                _hasPerm('projects') && !_navHidden('projects') && activeProduct === 'profiler' && renderNavButton(FolderOpen, 'Мои проекты', showProfilerProjects, () => navigate('profiler-projects')),
                _hasPerm('identifiers') && !_navHidden('identifiers') && activeProduct !== 'profiler' && renderNavButton(Fingerprint, 'Идентификаторы', showIdentifiers, () => navigate('identifiers')),
                _hasPerm('settings') && renderNavButton(Settings2, 'Настройки панели', showSettings, () => navigate('settings')),
                _hasPerm('tools') && !_navHidden('tools') && renderNavButton(Wrench, 'Инструменты', showTools, () => navigate('tools')),
                _hasPerm('events') && !_navHidden('events') && !IS_PROFILER_ONLY && renderNavButton(Newspaper, 'Важные события', showEvents, () => navigate('events'),
                  eventsUnread > 0 ? <Badge variant="outline" className="ml-auto h-4 px-1.5 text-[9px] tabular-nums border-primary/40 text-primary bg-primary/10">{eventsUnread}</Badge> : null),
                _hasPerm('ideas') && !_navHidden('ideas') && activeProduct !== 'profiler' && renderNavButton(Lightbulb, 'Идеи и предложения', showIdeas, () => navigate('ideas'),
                  <span className="ml-auto flex items-center gap-1">
                    {ideasUnreadReplies > 0 && (
                      <span title={`${ideasUnreadReplies} новых ответ(а)/(ов) от команды`} className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    )}
                    {ideasVotingCount > 0 && (
                      <Badge variant="secondary" className="h-4 px-1.5 text-[9px] tabular-nums">{ideasVotingCount}</Badge>
                    )}
                  </span>
                ),
                _hasPerm('bugs') && !_navHidden('bugs') && activeProduct !== 'profiler' && renderNavButton(Bug, 'Баги и ошибки', showBugs, () => navigate('bugs'),
                  <span className="ml-auto flex items-center gap-1">
                    {bugsUnreadReplies > 0 && (
                      <span title={`${bugsUnreadReplies} новых ответ(а)/(ов) от команды`} className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    )}
                    {bugsNewCount > 0 && (
                      <Badge variant="outline" className="h-4 px-1.5 text-[9px] tabular-nums border-red-500/30 text-red-400 bg-red-500/10">{bugsNewCount}</Badge>
                    )}
                  </span>
                ),
                /* Предзаказ серверов перенесён в Настройки → «Лицензия и оплата» */
              ].filter(Boolean)
              const adminItems = [
                _hasCentralPerm('licenses') && !_navHidden('licenses') && renderNavButton(Key, 'Лицензии', showLicenses, () => navigate('licenses')),
                _hasCentralPerm('work_servers') && !_navHidden('work_servers') && renderNavButton(Server, 'Рабочие серверы', showWorkServers, () => navigate('work-servers')),
                _hasCentralPerm('server_orders_admin') && !_navHidden('server_orders_admin') && renderNavButton(Server, 'Аренда серверов', showServerOrdersAdmin, () => navigate('server-orders-admin'),
                  ordersPaidPending > 0 ? (
                    <Badge className="ml-auto h-5 min-w-5 px-1.5 bg-emerald-500/15 text-emerald-500 border border-emerald-500/30 tabular-nums text-[10px]">
                      {ordersPaidPending}
                    </Badge>
                  ) : undefined
                ),
                _hasCentralPerm('deployments') && !_navHidden('deployments') && renderNavButton(Rocket, 'Деплой', showDeployments, () => navigate('deployments')),
                _hasPerm('internal_docs') && !_navHidden('internal_docs') && hasInternalDocs && renderNavButton(BookOpen, 'Документация', showDocs, () => navigate('docs')),
                _hasCentralPerm('fingerprints') && !_navHidden('fingerprints') && renderNavButton(Fingerprint, 'Отпечатки', showFingerprints, () => navigate('fingerprints')),
                _hasCentralPerm('identifiers_admin') && !_navHidden('identifiers_admin') && renderNavButton(IdCard, 'Управление идентификаторами', showIdentifiersAdmin, () => navigate('identifiers-admin')),
                _hasModerationPerm('ideas_admin') && !_navHidden('ideas_admin') && renderNavButton(Lightbulb, 'Идеи (модерация)', showIdeasAdmin, () => navigate('ideas-admin')),
                _hasModerationPerm('bugs_admin') && !_navHidden('bugs_admin') && renderNavButton(Bug, 'Баги (модерация)', showBugsAdmin, () => navigate('bugs-admin')),
                _hasCentralPerm('ai_tickets') && !_navHidden('ai_tickets') && renderNavButton(Sparkles, 'AI-тикеты', showAiTickets, () => navigate('ai-tickets-admin')),
                _hasModerationPerm('alarms') && !_navHidden('alarms') && renderNavButton(Megaphone, 'Новости и уведомления', showAlarmsAdmin, () => navigate('alarms-admin')),
                _canEditScenario && !_navHidden('scenario_editor') && renderNavButton(Workflow, 'Редактор сценария', showScenarioEditor, () => navigate('scenario-editor')),
              ].filter(Boolean)
              return (
                <>
                  {baseItems.length > 0 && (
                    <div className={`${sidebarCollapsed ? 'px-1.5' : 'px-2'} mb-1 flex flex-col gap-0.5`}>
                      {!sidebarCollapsed && <span className="px-3 pt-1 pb-2 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground/60">Навигация</span>}
                      {baseItems}
                    </div>
                  )}
                  {adminItems.length > 0 && (
                    <div className={`${sidebarCollapsed ? 'px-1.5' : 'px-2'} mb-1 flex flex-col gap-0.5`}>
                      {!sidebarCollapsed && <span className="px-3 pt-3 pb-2 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground/60">Администрирование</span>}
                      {adminItems}
                    </div>
                  )}
                </>
              )
            })()}

            {/* Projects list — only in booster mode, требует permission "projects" */}
            {!sidebarCollapsed && _hasPerm('projects') && activeProduct !== 'profiler' && (<>
              <Separator className="mx-3 my-2" />
              <div className="px-3 mb-2 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Проекты</span>
                    {activeProjects.length > 0 && <Badge variant="secondary" className="h-4 px-1.5 text-[9px] tabular-nums font-medium">{activeProjects.length}</Badge>}
                  </div>
                  {canEdit && <Button size="sm" onClick={() => setProjectModalOpen(true)} className="h-7 text-xs px-3">+ Создать проект</Button>}
                </div>
                {/* Idea #24: pill-filter for active/stopped/all */}
                {runningProjects.length > 0 && stoppedProjects.length > 0 && (
                  <div className="flex items-center gap-1 bg-muted/40 rounded-md p-0.5">
                    {([
                      { id: 'all' as const, label: 'Все', count: activeProjects.length },
                      { id: 'active' as const, label: 'Активные', count: runningProjects.length },
                      { id: 'stopped' as const, label: 'Стоп', count: stoppedProjects.length },
                    ]).map(f => (
                      <button key={f.id}
                        onClick={() => setProjectFilter(f.id)}
                        className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-all cursor-pointer ${
                          projectFilter === f.id
                            ? 'bg-background text-foreground shadow-sm border border-border/50'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {f.id === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                        {f.id === 'stopped' && <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />}
                        <span>{f.label}</span>
                        <span className="tabular-nums opacity-60">{f.count}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-1.5 flex-1 overflow-y-auto">
                {activeProjects.length === 0 && archivedProjects.length === 0 ? (
                  <div className="text-xs py-6 text-center text-muted-foreground">Создайте проект для каждого сайта</div>
                ) : (
                  <div className="flex flex-col gap-1">
                    {/* When effective filter=all, show grouped with headers; otherwise flat list */}
                    {_effectiveFilter === 'all' ? (<>
                      {runningProjects.length > 0 && stoppedProjects.length > 0 && (
                        <div className="px-2 pb-0.5 flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-success animate-pulse" /><span className="text-[9px] uppercase tracking-widest text-muted-foreground/50">Активные</span></div>
                      )}
                      {runningProjects.map((p, i) => renderProjectItem(p, i))}
                      {stoppedProjects.length > 0 && runningProjects.length > 0 && (
                        <div className="px-2 pt-1.5 pb-0.5 flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-muted-foreground/30" /><span className="text-[9px] uppercase tracking-widest text-muted-foreground/50">Остановленные</span></div>
                      )}
                      {stoppedProjects.map((p, i) => renderProjectItem(p, runningProjects.length + i))}
                    </>) : (
                      filteredProjects.map((p, i) => renderProjectItem(p, i))
                    )}

                    {archivedProjects.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-border">
                        <Button variant="ghost" onClick={() => setShowArchived(!showArchived)} className="w-full justify-between h-8 px-3 text-xs font-medium text-muted-foreground">
                          <span>Архив ({archivedProjects.length})</span>
                          <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showArchived ? 'rotate-180' : ''}`} />
                        </Button>
                        {showArchived && (
                          <div className="mt-1 flex flex-col gap-0.5 max-h-32 overflow-y-auto">
                            {archivedProjects.map((p, i) => renderProjectItem(p, i, true))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* File info bar */}
              {selectedProject && campaignFiles.sites_file && !(currentCampaign as any)?.archived && (
                <div className="px-3 py-2 shrink-0 cursor-pointer transition-colors hover:bg-accent border-t border-border" onClick={() => setFileEditorTarget(selectedProject)}>
                  <div className="text-xs flex items-center gap-3 text-muted-foreground">
                    <FileText className="w-3.5 h-3.5 shrink-0" />
                    <span>Сайтов: <strong className="text-foreground">{campaignFiles.sites_file.count}</strong></span>
                    {campaignFiles.key_files.length > 0 && <span>Ключей: <strong className="text-foreground">{campaignFiles.key_files.reduce((s: number, f: any) => s + f.keys_count, 0)}</strong></span>}
                    <span className="ml-auto text-xs text-muted-foreground">Открыть</span>
                  </div>
                </div>
              )}

              {/* Sidebar footer */}
              {appVersion && (
                <div className="mt-auto px-4 py-2.5 border-t border-sidebar-border flex items-center justify-between gap-2">
                  <div className="flex items-baseline gap-1.5 min-w-0">
                    <CreepLogo size={13} />
                    <span className="text-[11px] tracking-wider text-muted-foreground/60 font-medium shrink-0">v{appVersion}</span>
                  </div>
                  <button onClick={() => setShowChangelog(true)} className="text-[11px] text-primary/70 hover:text-primary transition-colors font-medium underline underline-offset-2 decoration-primary/30 hover:decoration-primary/60 shrink-0">История версий</button>
                </div>
              )}
            </>)}

            {/* Sidebar footer (профайлер): своя ветка VERSION.profiler, не бустерный VERSION.
                Статус сценария (опубл. / из кода) — в редакторе сценария, не тут. */}
            {HAS_PROFILER && !sidebarCollapsed && activeProduct === 'profiler' && (profilerVersion || appVersion) && (
              <div className="mt-auto px-4 py-2.5 border-t border-sidebar-border flex items-center justify-between gap-2">
                <div className="flex items-baseline gap-1.5 min-w-0">
                  <CreepLogo size={13} />
                  <span className="text-[11px] tracking-wider text-muted-foreground/60 font-medium shrink-0">v{profilerVersion || appVersion}</span>
                </div>
                <button onClick={() => setShowChangelog(true)} className="text-[11px] text-primary/70 hover:text-primary transition-colors font-medium underline underline-offset-2 decoration-primary/30 hover:decoration-primary/60 shrink-0">История версий</button>
              </div>
            )}

            {/* DEV-only движок Chrome 147/152 — самогейтится (пусто на клиентах) */}
            {!sidebarCollapsed && <DevEngineSwitcher collapsed={sidebarCollapsed} />}
          </TooltipProvider>
        </nav>

        {/* ── CONTENT AREA ── */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0 gap-1 md:gap-2">
          {!isOperator && displayProjectTabs.length > 0 && (
            <ProjectTabsBar
              tabs={displayProjectTabs}
              activeId={selectedProject}
              onSelect={handleSelectProject}
              onClose={handleCloseProjectTab}
            />
          )}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0 min-w-0 rounded-lg bg-card border border-border">
            {showSettings ? (
              <div key="settings" className="flex-1 overflow-y-auto"><PanelSettings accessLevel={accessLevel} isSuperAdmin={isSuperAdmin} isLicenseServer={isLicenseServer} appVersion={appVersion} onServerNameChange={(n: string) => { setServerName(n); localStorage.setItem('cb-server-name', n) }} /></div>
            ) : showTools ? (
              <div key="tools" className="flex-1 overflow-y-auto"><ToolsPage
                selectedTool={selectedToolSlug}
                onSelectChange={(slug) => {
                  if (slug) navigate({ tool: slug })
                  else navigate('tools')
                }}
              /></div>
            ) : showIdeas ? (
              <div key="ideas" className="flex-1 overflow-y-auto"><IdeasPage
                selectedId={selectedIdeaId}
                onSelectChange={(id) => {
                  if (id) navigate({ idea: id })
                  else navigate('ideas')
                }}
                onTitleResolved={setSelectedIdeaTitle}
                isSuperAdmin={isSuperAdmin}
                restricted={restrictIdeas}
              /></div>
            ) : showIdeasAdmin ? (
              <div key="ideas-admin" className="flex-1 overflow-y-auto"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><IdeasAdmin /></Suspense></div>
            ) : showBugs ? (
              <div key="bugs" className="flex-1 overflow-y-auto"><BugsPage
                selectedId={selectedBugId}
                onSelectChange={(id) => {
                  if (id) navigate({ bug: id })
                  else navigate('bugs')
                }}
                onTitleResolved={setSelectedBugTitle}
                isSuperAdmin={isSuperAdmin}
                restricted={restrictBugs}
              /></div>
            ) : showBugsAdmin ? (
              <div key="bugs-admin" className="flex-1 overflow-y-auto"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><BugsAdmin /></Suspense></div>
            ) : showAiTickets && !IS_BOOSTER_ONLY ? (
              <div key="ai-tickets-admin" className="flex-1 overflow-y-auto"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><AiTicketsAdmin /></Suspense></div>
            ) : showDocs ? (
              <div key="docs" className="flex-1 overflow-y-auto"><AdminDocs /></div>
            ) : showLicenses && !IS_BOOSTER_ONLY ? (
              <div key="licenses" className="flex-1 overflow-y-auto min-h-0"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><LicenseAdmin /></Suspense></div>
            ) : showWorkServers && !IS_BOOSTER_ONLY ? (
              <div key="work-servers" className="flex-1 overflow-y-auto min-h-0"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><WorkServersAdmin /></Suspense></div>
            ) : showAlarmsAdmin ? (
              <div key="alarms-admin" className="flex-1 overflow-y-auto min-h-0"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><NewsAdmin /></Suspense></div>
            ) : showScenarioEditor && !IS_BOOSTER_ONLY ? (
              <div key="scenario-editor" className="flex-1 overflow-y-auto min-h-0"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><ScenarioEditorAdmin /></Suspense></div>
            ) : showEvents ? (
              <div key="events" className="flex-1 overflow-y-auto"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><EventsPage
                selectedNewsId={selectedNewsId}
                onSelectNews={(id) => { if (id) navigate({ news: id }); else navigate('events') }}
                onTitleResolved={setSelectedNewsTitle}
                onUnreadChange={setEventsUnread}
              /></Suspense></div>
            ) : showDeployments && !IS_BOOSTER_ONLY ? (
              <div key="deployments" className="flex-1 overflow-y-auto"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><DeploymentManager /></Suspense></div>
            ) : showFingerprints && !IS_BOOSTER_ONLY ? (
              <div key="fingerprints" className="flex-1 overflow-y-auto"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><FingerprintsPage /></Suspense></div>
            ) : showIdentifiersAdmin && !IS_BOOSTER_ONLY ? (
              <div key="identifiers-admin" className="flex-1 overflow-y-auto"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><IdentifiersAdmin /></Suspense></div>
            ) : showIdentifiers ? (
              <div key="identifiers" className="flex-1 overflow-y-auto"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><IdentifiersPage /></Suspense></div>
            ) : showServerOrders ? (
              <div key="server-orders" className="flex-1 overflow-y-auto"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><ServerOrdersPage /></Suspense></div>
            ) : showServerOrdersAdmin && !IS_BOOSTER_ONLY ? (
              <div key="server-orders-admin" className="flex-1 overflow-y-auto"><Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Загрузка...</div>}><ServerOrdersAdmin /></Suspense></div>
            ) : showProjects ? (
              <div key="projects" className="flex-1 overflow-y-auto"><ProjectsPage projects={projects} onSelectProject={handleSelectProject} onNewProject={() => setProjectModalOpen(true)} onRefresh={refreshProjects} onAlert={setAlert} canFull={canFull} canEdit={canEdit} hourlyHistory={hourlyHistory} hourlyCampaignHistory={hourlyCampaignHistory} /></div>
            ) : activeProduct === 'profiler' ? (
              showProfilerProjects ? (
                <div key="profiler-projects" className="flex-1 overflow-y-auto">
                  <ProfilerProjectsPage
                    projects={projects}
                    onSelectProject={handleSelectProject}
                    onNewProject={() => setProjectModalOpen(true)}
                    onRefresh={refreshProjects}
                    onAlert={setAlert}
                    canFull={canFull}
                    canEdit={canEdit}
                    hourlyHistory={hourlyHistory}
                    hourlyCampaignHistory={hourlyCampaignHistory}
                  />
                </div>
              ) : showProfilerOverview || (!selectedProject && rightTab !== 'results') ? (
                <div key="profiler-overview" className="flex-1 overflow-y-auto">
                  <ProfilerOverview
                    stats={wsStats}
                    timeline={timeline}
                    hourlyHistory={hourlyHistory}
                    hourlyCampaignHistory={hourlyCampaignHistory}
                    campaignStats={campaignStats}
                    projects={projects}
                    sysInfo={sysInfo}
                    onSelectProject={handleSelectProject}
                    onNavigate={navigate}
                  />
                </div>
              ) : rightTab === 'results' && profilerRunning ? (
                <div key="profiler-results" className="flex-1 flex flex-col min-h-0 min-w-0">
                  {renderToolbar()}
                  <div className="flex-1 overflow-y-auto overflow-x-hidden relative min-w-0">
                    <div className="max-w-6xl w-full min-w-0 box-border p-2 md:p-6">
                      <ResultsStats stats={resultStats} mode="profiler" />
                      <WorkersTable workers={projectWorkers} startedAt={undefined} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col min-h-0 min-w-0">
                  {renderToolbar()}
                  <div className="flex-1 flex flex-col md:flex-row min-h-0 min-w-0">
                    {/* Section tabs — horizontal scroll on mobile, vertical sidebar on desktop */}
                    <div className="shrink-0 md:w-[280px] flex md:flex-col py-2 md:py-3 overflow-x-auto md:overflow-x-hidden md:overflow-y-auto border-b md:border-b-0 md:border-r border-border/50 min-w-0 bg-muted/80 md:bg-transparent">
                      <div className="flex md:flex-col gap-1.5 md:gap-1 mx-2 md:mx-2 pr-1 md:pr-0">
                        {visibleSections.map(section => {
                          const isActive = selectedSection === section
                          const Icon = SECTION_ICONS[section] || Settings2
                          return (
                            <Button key={section} variant="ghost" onClick={() => setSelectedSection(section)}
                              className={`justify-start gap-1.5 md:gap-2.5 h-9 md:h-10 text-[12px] md:text-sm font-medium rounded-md cursor-pointer whitespace-nowrap px-3 md:px-4 shrink-0 border md:border-0 ${isActive ? 'bg-primary/20 text-primary border-primary/40 shadow-sm md:bg-accent md:text-accent-foreground md:border-transparent md:shadow-none font-semibold' : 'bg-background/90 border-border text-foreground/80 hover:text-foreground hover:bg-background md:bg-transparent md:border-transparent md:text-muted-foreground md:hover:bg-accent/50'}`}>
                              <Icon className={`w-3.5 md:w-4 h-3.5 md:h-4 shrink-0 ${isActive ? 'opacity-100' : 'opacity-60'}`} />
                              {section}
                            </Button>
                          )
                        })}
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
                      <div className="p-2 md:p-6 max-w-3xl w-full min-w-0 box-border">
                        <DynamicTabProfiler section={selectedSection as ProfilerSectionName} cfg={activeCfg} update={handleCfgUpdate} />
                      </div>
                    </div>
                  </div>
                </div>
              )
            ) : selectedProject ? (
              rightTab === 'results' && (campaignRunning || campaignPaused || hasRunData) ? (
                <div key={`project-results-${selectedProject}`} className="flex-1 flex flex-col min-h-0 min-w-0">
                  {renderToolbar()}
                  <div className="flex-1 overflow-y-auto overflow-x-hidden relative min-w-0">
                    {actionInProgress && (
                      <div className="absolute inset-0 z-10 bg-background/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-3 animate-in fade-in duration-150">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        <span className="text-sm font-medium text-muted-foreground">
                          {actionInProgress === 'start' ? 'Запуск' : 'Остановка'} {selectedProjectName && `«${selectedProjectName}»`}...
                        </span>
                      </div>
                    )}
                    <div className="max-w-6xl w-full min-w-0 box-border p-2 md:p-6">
                      <ResultsStats stats={resultStats} />
                      <WorkersTable workers={projectWorkers} startedAt={(currentCampaign as any)?.started_at} />
                    </div>
                  </div>
                </div>
              ) : cfgLoading ? (
                <div className="flex-1 flex flex-col min-h-0 min-w-0 p-3 md:p-6 gap-3 md:gap-4 animate-in fade-in duration-150">
                  {/* Header skeleton */}
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-7 md:h-8 rounded-full bg-primary/30 animate-pulse" />
                    <div className="h-6 md:h-7 bg-muted/60 rounded-md animate-pulse w-1/2 max-w-xs" />
                    <div className="h-5 w-20 bg-muted/50 rounded-full animate-pulse ml-auto" />
                  </div>
                  {/* Button row skeleton */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="h-9 w-24 bg-primary/20 rounded-md animate-pulse" />
                    <div className="h-9 w-28 bg-muted/60 rounded-md animate-pulse" />
                    <div className="h-9 w-9 bg-muted/40 rounded-md animate-pulse md:hidden" />
                  </div>
                  {/* Tabs row skeleton */}
                  <div className="h-11 bg-muted/60 rounded-lg animate-pulse w-full md:w-64" />
                  {/* Content skeleton */}
                  <div className="flex gap-4 min-h-0 flex-1">
                    <div className="hidden md:flex flex-col gap-2 w-[260px] shrink-0">
                      {[0,1,2,3,4,5].map(i => <div key={i} className="h-10 bg-muted/50 rounded-md animate-pulse" />)}
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="h-8 bg-muted/50 rounded-md animate-pulse w-2/3" />
                      <div className="h-20 bg-muted/40 rounded-md animate-pulse" />
                      <div className="h-20 bg-muted/40 rounded-md animate-pulse" />
                      <div className="h-32 bg-muted/40 rounded-md animate-pulse" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                    <span>Загрузка проекта {selectedProjectName && `«${selectedProjectName}»`}...</span>
                  </div>
                </div>
              ) : (
                // Mobile: single scroll for the whole thing. Header scrolls away;
                //   section tabs are sticky to the top of that scroll.
                // Desktop: header fixed at top, tabs as side-nav, content with its own scroll.
                <div key={`project-settings-${selectedProject}`} className="flex-1 flex flex-col min-h-0 min-w-0 relative overflow-y-auto md:overflow-hidden">
                  {actionInProgress && (
                    <div className="absolute inset-0 z-10 bg-background/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-3 animate-in fade-in duration-150">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <span className="text-sm font-medium text-muted-foreground">
                        {actionInProgress === 'start' ? 'Запуск' : 'Остановка'} {selectedProjectName && `«${selectedProjectName}»`}...
                      </span>
                    </div>
                  )}
                  {renderToolbar()}
                  <div className="md:flex-1 flex flex-col md:flex-row md:min-h-0 md:min-w-0">
                    {/* Section tabs */}
                    <div className="shrink-0 md:w-[280px] flex md:flex-col py-2 md:py-3 overflow-x-auto md:overflow-x-hidden md:overflow-y-auto border-b md:border-b-0 md:border-r border-border/50 min-w-0 bg-muted/95 md:bg-transparent sticky top-0 z-20 md:static md:z-auto">
                      <div className="flex md:flex-col gap-1.5 md:gap-1 mx-2 md:mx-2 pr-1 md:pr-0">
                        {visibleSections.map(section => {
                          const isActive = selectedSection === section
                          const Icon = SECTION_ICONS[section] || Settings2
                          return (
                            <Button key={section} variant="ghost" onClick={() => setSelectedSection(section)}
                              className={`justify-start gap-1.5 md:gap-2.5 h-9 md:h-10 text-[12px] md:text-sm font-medium rounded-md cursor-pointer whitespace-nowrap px-3 md:px-4 shrink-0 border md:border-0 ${isActive ? 'bg-primary/20 text-primary border-primary/40 shadow-sm md:bg-accent md:text-accent-foreground md:border-transparent md:shadow-none font-semibold' : 'bg-background/90 border-border text-foreground/80 hover:text-foreground hover:bg-background md:bg-transparent md:border-transparent md:text-muted-foreground md:hover:bg-accent/50'}`}>
                              <Icon className={`w-3.5 md:w-4 h-3.5 md:h-4 shrink-0 ${isActive ? 'opacity-100' : 'opacity-60'}`} />
                              {section}
                            </Button>
                          )
                        })}
                      </div>
                    </div>
                    {/* Section content */}
                    <div className="md:flex-1 md:overflow-y-auto md:min-w-0">
                      <div className="p-2 md:p-6 max-w-3xl w-full min-w-0 box-border">
                        {activeProduct === 'profiler'
                          ? <DynamicTabProfiler section={selectedSection as ProfilerSectionName} cfg={activeCfg} update={handleCfgUpdate} />
                          : <DynamicTabBooster section={selectedSection as BoosterSectionName} cfg={activeCfg} update={handleCfgUpdate} uploadBlock={renderUploadBlock} isAdmin={isSuperAdmin} campaignId={selectedProject || undefined} />}
                      </div>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="flex-1 overflow-y-auto">
                <Suspense fallback={<div className="flex-1 flex items-center justify-center p-8 text-muted-foreground">Загрузка...</div>}>
                  <BoosterOverview stats={wsStats} timeline={timeline} hourlyHistory={hourlyHistory} hourlyCampaignHistory={hourlyCampaignHistory} campaignStats={campaignStats} projects={projects} sysInfo={sysInfo} onSelectProject={handleSelectProject} onNavigate={navigate} />
                </Suspense>
              </div>
            )}
          </div>

          {/* Bottom panel — только когда выбран конкретный проект */}
          {!showSettings && !showTools && !showDocs && !showLicenses && !showDeployments && selectedProject && (
            <div className="shrink-0 rounded-lg bg-card border border-border overflow-hidden min-w-0">
              <BottomPanel logs={filteredLogs} getAllLogs={getAllLogs} onClearLogs={() => {
                setLogs(selectedProject ? prev => prev.filter(e => e.campaign_id !== selectedProject) : [])
                if (!selectedProject) allLogsRef.current = []
                else allLogsRef.current = allLogsRef.current.filter(e => e.campaign_id !== selectedProject)
              }} wsState={wsState} cfg={activeCfg}
                running={activeProduct === 'profiler' ? profilerRunning : campaignRunning} stats={enrichedStats} mode={activeProduct === 'profiler' ? 'profiler' : 'booster'} campaignId={selectedProject ?? undefined} actionInProgress={actionInProgress} actionProjectName={selectedProjectName} elapsedSec={(wsStats as any)?.booster_elapsed_sec} />
            </div>
          )}
        </div>
      </div>

      {/* ── MODALS ── */}
      {activeProduct === 'profiler' ? (
        <ProfilerProjectModal open={projectModalOpen} mode="create" onClose={() => setProjectModalOpen(false)}
          onSubmit={async (data) => {
            const res = await createProfilerCampaignApi({ name: data.name, comment: data.comment, color: data.color, schedule: data.schedule, settings: { ...getProfilerDefaults(), ...profilerCfg } })
            if (res.error) { setAlert({ type: 'error', text: res.error }); return }
            refreshProjects()
            if (res.id) handleSelectProject(res.id)
            setProjectModalOpen(false)
          }}
        />
      ) : (
        <ProjectModal open={projectModalOpen} mode="create" onClose={() => setProjectModalOpen(false)}
          onSubmit={async (data: ProjectModalResult) => {
            const res = await createCampaignApi({ name: data.name, comment: data.comment, color: data.color, schedule: data.schedule, settings: selectedCfg })
            if (res.error) { setAlert({ type: 'error', text: res.error }); return }
            refreshProjects()
            if (res.id) handleSelectProject(res.id)
            setProjectModalOpen(false)
          }}
        />
      )}

      {editingProjectId && editingProject && (
        activeProduct === 'profiler' ? (
          <ProfilerProjectModal open mode="edit" onClose={() => { setEditingProjectId(null); setEditingProject(null) }}
            onSubmit={async (data) => {
              await updateProfilerCampaign(editingProjectId, { name: data.name, comment: data.comment, color: data.color, schedule: data.schedule })
              refreshProjects()
              if (selectedProject === editingProjectId) loadProject(editingProjectId)
              setEditingProjectId(null); setEditingProject(null)
            }}
            onArchive={async (archived: boolean) => {
              await updateProfilerCampaign(editingProjectId, { archived })
              refreshProjects()
              if (archived && selectedProject === editingProjectId) navigate('overview')
              setEditingProjectId(null); setEditingProject(null)
            }}
            initial={{ name: editingProject.name, comment: editingProject.comment || '', color: editingProject.color || '', archived: editingProject.archived, schedule: {
              enabled: editingProject.sched_enabled ?? false,
              type: (editingProject.sched_type as any) || 'time',
              start_time: editingProject.sched_start_time || '09:00',
              days: editingProject.sched_days || 'Каждый день',
              days_custom: editingProject.sched_days_custom || '',
              work_from: editingProject.sched_work_from || '00:00',
              work_to: editingProject.sched_work_to || '23:59',
              run_hours: editingProject.sched_run_hours ?? 0,
              run_minutes: editingProject.sched_run_minutes ?? 0,
              interval_minutes: editingProject.sched_interval_minutes ?? 10,
              max_runs: editingProject.sched_restart_max ?? 0,
              restart_delay: editingProject.sched_restart_delay ?? 5,
              hourly_minute: editingProject.sched_hourly_minute ?? 0,
              // Bug #193: прокидываем «Случайное время старта» в диалог
              // расписания, иначе чекбокс всегда выключен и сохранение
              // через edit-modal сбрасывает SCHED_START_RANDOM.
              start_random: editingProject.sched_start_random ?? false,
              start_time_to: editingProject.sched_start_time_to || '',
            } }}
          />
        ) : (
          <ProjectModal open mode="edit" onClose={() => { setEditingProjectId(null); setEditingProject(null) }}
            onSubmit={async (data: ProjectModalResult) => {
              await updateCampaign(editingProjectId, { name: data.name, comment: data.comment, color: data.color, schedule: data.schedule })
              refreshProjects()
              if (selectedProject === editingProjectId) loadProject(editingProjectId)
              setEditingProjectId(null); setEditingProject(null)
            }}
            onArchive={async (archived: boolean) => {
              await updateCampaign(editingProjectId, { archived })
              refreshProjects()
              if (archived && selectedProject === editingProjectId) navigate('overview')
              setEditingProjectId(null); setEditingProject(null)
            }}
            initial={{ name: editingProject.name, comment: editingProject.comment || '', color: editingProject.color || '', archived: editingProject.archived, schedule: {
              enabled: editingProject.sched_enabled ?? false,
              type: (editingProject.sched_type as any) || 'time',
              start_time: editingProject.sched_start_time || '09:00',
              days: editingProject.sched_days || 'Каждый день',
              days_custom: editingProject.sched_days_custom || '',
              work_from: editingProject.sched_work_from || '00:00',
              work_to: editingProject.sched_work_to || '23:59',
              run_hours: editingProject.sched_run_hours ?? 0,
              run_minutes: editingProject.sched_run_minutes ?? 0,
              interval_minutes: editingProject.sched_interval_minutes ?? 10,
              max_runs: editingProject.sched_restart_max ?? 0,
              restart_delay: editingProject.sched_restart_delay ?? 5,
              hourly_minute: editingProject.sched_hourly_minute ?? 0,
              // Bug #193: прокидываем «Случайное время старта» в диалог
              // расписания, иначе чекбокс всегда выключен и сохранение
              // через edit-modal сбрасывает SCHED_START_RANDOM.
              start_random: editingProject.sched_start_random ?? false,
              start_time_to: editingProject.sched_start_time_to || '',
            } }}
          />
        )
      )}

      {showSchedule && selectedProject && (
        <SchedulerDialog open={showSchedule} onClose={() => setShowSchedule(false)}
          initial={(() => {
            const c = selectedCfg || {}
            return {
              enabled: c.SCHED_ENABLED === 'Да',
              type: (c.SCHED_TYPE as any) || 'time',
              start_time: c.SCHED_START_TIME || '09:00',
              start_random: c.SCHED_START_RANDOM === 'Да',
              start_time_to: c.SCHED_START_TIME_TO || '',
              days: c.SCHED_DAYS || 'Каждый день',
              days_custom: c.SCHED_DAYS_CUSTOM || '',
              work_from: c.SCHED_WORK_FROM || '00:00',
              work_to: c.SCHED_WORK_TO || '23:59',
              run_hours: Number(c.SCHED_RUN_HOURS) || 0,
              run_minutes: Number(c.SCHED_RUN_MINUTES) || 0,
              interval_minutes: Number(c.SCHED_INTERVAL_MINUTES) || 10,
              max_runs: Number(c.SCHED_RESTART_MAX) || 0,
              restart: c.SCHED_RESTART === 'Да',
              restart_delay: Number(c.SCHED_RESTART_DELAY) || 5,
              hourly_minute: Number(c.SCHED_HOURLY_MINUTE) || 0,
            }
          })()}
          onSave={async (schedData: any) => {
            const merged: Settings = {
              ...selectedCfg || {},
              SCHED_ENABLED: schedData.enabled ? 'Да' : 'Нет',
              SCHED_TYPE: schedData.type || 'time',
              SCHED_START_TIME: schedData.start_time || '09:00',
              SCHED_START_RANDOM: schedData.start_random ? 'Да' : '',
              SCHED_START_TIME_TO: schedData.start_time_to || '',
              SCHED_DAYS: schedData.days || 'Каждый день',
              SCHED_DAYS_CUSTOM: schedData.days_custom || '',
              SCHED_WORK_FROM: schedData.work_from || '00:00',
              SCHED_WORK_TO: schedData.work_to || '23:59',
              SCHED_RUN_HOURS: String(schedData.run_hours || 0),
              SCHED_RUN_MINUTES: String(schedData.run_minutes || 0),
              SCHED_INTERVAL_MINUTES: String(schedData.interval_minutes || 10),
              SCHED_RESTART: schedData.restart ? 'Да' : 'Нет',
              SCHED_RESTART_DELAY: String(schedData.restart_delay || 5),
              SCHED_RESTART_MAX: String(schedData.restart_max || 0),
              SCHED_HOURLY_MINUTE: String(schedData.hourly_minute || 0),
            }
            setSelectedCfg(merged)
            await updateCampaign(selectedProject, { schedule: schedData })
            // Bug #105: invalidate cache чтобы при следующем открытии
            // SchedulerDialog/проекта мы перечитали свежие SCHED_TYPE
            // и др. поля. Без этого через 60s кэш отдавал старый
            // SCHED_TYPE='time' и continuous-режим «не сохранялся».
            delete projectCacheRef.current[selectedProject]
            refreshProjects()
          }}
        />
      )}

      <AlertDialog open={!!deleteProjectId} onOpenChange={(open) => { if (!open) setDeleteProjectId(null) }}>
        <AlertDialogContent className="max-w-xs">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить проект?</AlertDialogTitle>
            <AlertDialogDescription>{projects.find(p => p.id === deleteProjectId)?.name || 'Проект'} — будет удалён безвозвратно</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={async () => {
              const id = deleteProjectId!; setDeleteProjectId(null); await deleteCampaign(id)
              setProjectTabs((prev) => prev.filter((t) => t.id !== id))
              refreshProjects(); if (selectedProject === id) navigate('overview')
            }}>Удалить</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {fileEditorTarget && selectedProject && (
        <Suspense fallback={null}>
          <FilesEditorModal campaignId={fileEditorTarget} onClose={() => setFileEditorTarget(null)} onSaved={() => {
            getCampaignFiles(selectedProject).then(setCampaignFiles).catch(() => {})
          }} />
        </Suspense>
      )}

      {taskGeneratorTarget && selectedProject && (
        <Suspense fallback={null}>
          <TaskGeneratorModal campaignId={taskGeneratorTarget} onClose={() => setTaskGeneratorTarget(null)} onSaved={() => {
            delete projectCacheRef.current[selectedProject]; getCampaignFiles(selectedProject).then(setCampaignFiles).catch(() => {})
          }} />
        </Suspense>
      )}

      {/* Floating toast */}
      {alert && (
        <div className="fixed bottom-4 left-4 right-4 z-50 animate-slide-up max-w-none md:left-auto md:right-4 md:max-w-sm">
          <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 shadow-2xl backdrop-blur-2xl backdrop-saturate-150 ${
            alert.type === 'success' ? 'border-white/10 bg-white/[0.04] text-success' : 'border-white/10 bg-white/[0.04] text-destructive'
          }`} style={{ WebkitBackdropFilter: 'blur(40px) saturate(1.5)' }}>
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium">{alert.type === 'success' ? 'Успешно' : 'Ошибка'}</p>
              <p className="text-xs text-white/50 mt-0.5">{alert.text}</p>
            </div>
          </div>
        </div>
      )}

      <ChangelogModal open={showChangelog} onClose={() => setShowChangelog(false)} currentVersion={appVersion} />

      {/* Live admin notification — persistent until user acks. */}
      <LiveNotificationToast
        pending={livePushQueue}
        onAck={(id) => {
          livePushDismissedRef.current.add(id)
          setLivePushQueue(prev => prev.filter(p => p.id !== id))
          setEventsUnread(c => Math.max(0, c - 1))
        }}
      />

      {/* Styled confirm dialog (replaces native window.confirm). */}
      <ConfirmDialogHost />

      {/* Опциональный эстетический эффект «Магические движения» (pref cb-magic-motion).
          Сам себя гейтит — рендерит canvas только когда включено в настройках. */}
      <MagicCursorCanvas />

      {/* AI assistant — floating chat widget, context-aware about selected project. */}
      <AssistantWidget
        currentProject={selectedProject ? (() => {
          const p = projects.find(x => x.id === selectedProject)
          if (!p) return null
          const status = String(p.status || '').toLowerCase()
          return {id: p.id, name: p.name || p.id, running: status === 'running' || status === 'active' || status === 'started'}
        })() : null}
      />
    </div>
  )
}
